import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import api from './shared/api/request'
import { DepartmentContext, useAuthStore } from './shared/context/authStore'
import ScheduleWorkspace from './features/schedule/ScheduleWorkspace'
import HumanVerification, {
  HumanVerificationAction,
  HumanVerificationConfig
} from './shared/components/HumanVerification'
import loginBrandMark from './assets/buwuyun-brand-mark.png'
import loginCampusImage from './assets/login-campus-collaboration.jpg'
import {
  AccountDeletionPanel,
  AccountDeletionGate,
  DepartmentVerificationGate,
  LegalConsent,
  LegalDocumentPage,
  LegalGate,
  LegalVersions
} from './features/legal/LegalExperience'
import { DepartmentBilling, PlatformBilling } from './features/commercial/CommercialExperience'

type ApiResult<T> = { success: boolean; data: T; error?: string; message?: string; pagination?: { total: number } }
type User = {
  id: string
  username: string
  email?: string
  fullName: string
  role: string
  studentId?: string
  faculty?: string
  className?: string
  phone?: string
  joinedAt?: string
  notes?: string
  status?: 'active' | 'disabled' | 'departed'
  phoneVisibility?: 'department' | 'managers'
  emailVisibility?: 'department' | 'managers'
  departedAt?: string | null
  departureReason?: string
  lastLoginAt?: string | null
  departmentId?: string
  mustChangePassword?: boolean
  legalAcceptanceRequired?: boolean
  accountDeletionPending?: boolean
  accountDeletionScheduledAt?: string | null
  departmentStatus?: 'active' | 'pending_verification' | 'disabled'
  verificationStatus?: 'unverified' | 'pending' | 'verified' | 'rejected'
  subscriptionStatus?: 'legacy' | 'trial_active' | 'trial_readonly' | 'activation_pending' | 'formal' | 'expired'
  activeAssignments?: Assignment[]
}
type Assignment = { id: string; user_id: string; user_name: string; role: string; role_name: string; status: string; starts_at: string; ends_at?: string | null; category?: 'minister' | 'deputy' | 'functional' | 'member'; permissions?: string[] }
type Task = { id: string; title: string; description?: string; assignee_id?: string; assignee_name?: string; creator_name?: string; priority: string; status: string; progress?: number; due_date?: string; activity_id?: string | null; activity_name?: string | null; activity_participant_scope?: 'registered_only' | 'department_members'; activity_worker_type?: 'participant' | 'staff' }
type Notice = { id: string; title: string; content: string; type: string; author_name?: string; created_at: string }
type Activity = {
  id: string
  name: string
  description?: string
  type?: 'team_building' | 'work' | 'other'
  startDate?: string
  start_date?: string
  endDate?: string
  budget?: number
  actualCost?: number
  actual_cost?: number
  status: string
  location?: string
  organizerName?: string
  signupEnabled?: boolean
  signupLimit?: number | null
  signupDeadline?: string | null
  feeEnabled?: boolean
  feeAmount?: number
  registrationCount?: number
  myRegistrationStatus?: string | null
  taskCount?: number
  openTaskCount?: number
  schedulePlanCount?: number
  feeSummary?: { dueAmount: number; paidAmount: number; unpaidAmount: number; unpaidCount: number }
}
type ActivityRegistration = { id: string; userId: string; userName: string; status: string; createdAt: string; canSelfCancel?: boolean }
type ActivityFeeRecord = { id: string; userId: string; userName: string; dueAmount: number; paidAmount: number; status: string; note?: string }
type ActivityExitRequest = { id: string; user_name: string; reason?: string; status: string; created_at: string; review_note?: string }
type ActivitySchedulePlan = { id: string; name: string; status: string; location?: string; shiftCount: number; createdAt: string }
type ActivityWorker = { userId: string; userName: string; taskCount: number }
type ActivityDetail = {
  activity: Activity
  registrations: ActivityRegistration[]
  myRegistration: ActivityRegistration | null
  feeRecords: ActivityFeeRecord[]
  exitRequests: ActivityExitRequest[]
  workers: ActivityWorker[]
  tasks: Task[]
  schedulePlans: ActivitySchedulePlan[]
  canManage: boolean
}
type Transaction = {
  id: string
  title: string
  amount: number
  type: 'income' | 'expense'
  category: string
  date: string
  approvalStatus: string
  approvalReasons?: string[]
  approvalReasonText?: string[]
  reviewNote?: string
  description?: string
  activityId?: string | null
  counterpartyUserId?: string | null
  counterpartyName?: string
  proofStatus?: string
}
type Appointment = { id: string; target_user_name: string; requested_role: string; requested_role_name: string; requested_by?: string; requested_by_name: string; reason?: string; sensitive: boolean; status: string; created_at: string }
type TaskRequest = { id: string; task_id: string; task_title: string; requester_name: string; type: string; reason: string; status: string; created_at: string }
type RegistrationRequest = { id: string; fullName: string; studentId: string; faculty?: string; className?: string; phone?: string; email?: string; reason?: string; status: string; createdAt: string; reviewedAt?: string; rejectReason?: string }
type ImportResult = { createdCount: number; skippedCount: number; created: Array<User & { initialPassword: string }>; skipped: Array<{ row: number; studentId?: string; fullName?: string; reason: string }> }
type Governance = { templates: Record<string, { name: string; description: string; level: number; category: string; permissions: string[] }>; sensitivePermissions: string[]; users: Array<User & { permissions: string[] }>; assignments: Assignment[]; appointmentRequests: Appointment[]; proxies: any[]; auditLogs: any[] }
type MemberProfile = { member: User; assignments: Assignment[]; appointmentRequests: Appointment[]; workSummary: { taskCount: number; openTaskCount: number; completedTaskCount: number; scheduleCount: number } | null; auditLogs: any[]; canManage: boolean; isSelf: boolean }
type Backup = { filename: string; size: number; createdAt: string }
type RemoteAccessStatus = {
  configured: boolean
  state: 'not_configured' | 'stopped' | 'connecting' | 'online' | 'error'
  enabled: boolean
  publicUrl: string | null
  departmentSlug: string | null
  deploymentId: string | null
  tunnelId: string | null
  bridgePort: number
  bridgeAvailable: boolean
  binaryAvailable: boolean
  lastStartedAt: string | null
  lastOnlineAt: string | null
  lastError: string | null
  logs: string[]
}
type Dashboard = {
  department: { id: string; name: string; faculty: string; headName: string }
  user: { id: string; username: string; role: string; roleName: string; permissions: string[]; isManager: boolean; activeProxy?: any }
  metrics: { members: number; totalTasks: number; myOpenTasks: number; overdueTasks: number; pendingApprovals: number; upcomingActivities: number; todaySchedule: number }
  tasks: { mine: Task[]; overdueCount: number; status: { pending: number; inProgress: number; completed: number } }
  approvals: { appointments: Appointment[]; transactions: any[]; taskRequests: TaskRequest[] }
  schedule: { mine: any[]; today: any[] }
  activities: Activity[]
  finance: {
    totalIncome: number
    totalExpense: number
    netIncome: number
    budget: number
    spent: number
    pendingExpense?: number
    committedExpense?: number
    remaining: number
    financialStartDate?: string | null
    financialStartDatePending?: boolean
  }
  notices: Notice[]
  system: { storage: string; databaseSize: number; databaseUpdatedAt: string | null; backupCount: number; lanMode: boolean }
}

type DepartmentProfile = {
  name: string
  faculty?: string
  description?: string
}

type Tab = 'overview' | 'tasks' | 'schedule' | 'activities' | 'finance' | 'members' | 'approvals' | 'handover' | 'plan' | 'settings'

const tabInfo: Array<{ key: Tab; label: string; mark: string; permission?: string; manager?: boolean; admin?: boolean }> = [
  { key: 'overview', label: '部门总览', mark: '总' },
  { key: 'tasks', label: '任务', mark: '任', permission: 'tasks:read' },
  { key: 'schedule', label: '排班', mark: '排', permission: 'schedule:read' },
  { key: 'activities', label: '活动', mark: '活', permission: 'activities:read' },
  { key: 'finance', label: '财务', mark: '财', permission: 'finance:read' },
  { key: 'members', label: '成员', mark: '员' },
  { key: 'approvals', label: '审批', mark: '审', manager: true },
  { key: 'handover', label: '资料与交接', mark: '交', admin: true },
  { key: 'plan', label: '套餐与开通', mark: '套', manager: true },
  { key: 'settings', label: '系统设置', mark: '设', admin: true }
]

const statusLabel: Record<string, string> = {
  pending: '待处理',
  in_progress: '进行中',
  completed: '已完成',
  approved: '已通过',
  rejected: '已驳回',
  planning: '筹备中',
  ongoing: '进行中',
  active: '进行中',
  pending_verification: '待核验',
  unverified: '未认证',
  verified: '已认证',
  legacy: '历史正式',
  trial_active: '试用中',
  trial_readonly: '试用到期',
  activation_pending: '开通处理中',
  formal: '正式套餐',
  expired: '套餐到期',
  disabled: '已停用',
  departed: '已离部',
  ended: '已结束',
  suspended: '已暂停',
  cancelled: '已取消',
  exited: '已退出',
  unpaid: '未缴',
  partial: '部分缴费',
  paid: '已缴',
  low: '普通',
  medium: '重要',
  high: '紧急',
  leave: '请假',
  swap: '换班',
  extension: '延期'
}

const roleLabel: Record<string, string> = {
  admin: '部长/系统管理员',
  deputy: '副部长',
  finance: '财务负责人',
  activity: '活动负责人',
  member: '普通成员'
}

const permissionLabel: Record<string, string> = {
  'dashboard:read': '查看部门总览', 'finance:read': '查看财务', 'finance:write': '登记财务', 'finance:approve': '审批财务',
  'users:read': '查看完整成员资料', 'users:write': '管理成员', 'activities:read': '查看活动', 'activities:write': '管理活动',
  'projects:read': '查看项目', 'projects:write': '管理项目', 'reports:read': '查看报表', 'tasks:read': '查看任务',
  'tasks:write': '管理任务', 'schedule:read': '查看排班', 'schedule:write': '管理排班', 'notices:read': '查看通知',
  'notices:write': '发布通知', 'governance:read': '查看任职', 'governance:write': '发起任命',
  'governance:approve': '审批任命', admin: '系统与交接管理'
}

const money = (value: number | undefined) => `¥${Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function localDateText(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const todayDate = () => localDateText()
const todayDateTime = () => `${todayDate()}T18:00`
const defaultDateText = () => `${todayDate()}（系统默认）`

function dateText(value?: string | null, options: { defaultToday?: boolean; dateOnly?: boolean } = {}) {
  if (!value) return options.defaultToday ? defaultDateText() : '未设置'
  const parsed = new Date(options.dateOnly ? `${value.slice(0, 10)}T00:00:00` : value)
  if (Number.isNaN(parsed.getTime())) return options.defaultToday ? defaultDateText() : value
  if (options.dateOnly) return localDateText(parsed)
  return parsed.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function financialStartText(finance: Dashboard['finance']) {
  const date = finance.financialStartDate || todayDate()
  return finance.financialStartDatePending ? `${date}（首笔账后确定）` : dateText(date, { dateOnly: true, defaultToday: true })
}

function errorText(error: unknown) {
  if (typeof error === 'object' && error && 'error' in error) return String((error as { error?: string }).error || '操作失败')
  return typeof error === 'string' ? error : '操作失败，请稍后重试。'
}

async function getData<T>(url: string): Promise<T> {
  const result = await api.get(url) as unknown as ApiResult<T>
  return result.data
}

function Badge({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{statusLabel[value] || value}</span>
}

function Empty({ text }: { text: string }) {
  return <div className="empty"><span>暂无</span><p>{text}</p></div>
}

function Section({ title, action, children, className = '' }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}><header className="panel-head"><h2>{title}</h2>{action}</header>{children}</section>
}

function csvEscape(value: unknown) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function LoginVisual() {
  return <div className="login-brand" aria-label="部务云校园协作场景" style={{ backgroundImage: `url(${loginCampusImage})` }}>
    <span className="brand-seal"><img src={loginBrandMark} alt="" loading="eager" decoding="sync" /></span>
    <div><strong>部务云</strong><small>校园部门协作平台</small></div>
  </div>
}

type DirectorySchool = { id: string; name: string; province: string; city: string; level: string; nature: string }
type DirectoryDepartment = { id: string; schoolId: string; organizationName: string; name: string; displayName: string }
type LoginSchoolOption = Pick<DirectorySchool, 'id' | 'name'> & { code: string }

function SchoolSearch({ value, onChange }: { value: DirectorySchool | null; onChange: (value: DirectorySchool | null) => void }) {
  const [query, setQuery] = useState(value?.name || '')
  const [results, setResults] = useState<DirectorySchool[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const keyword = query.trim()
    if (keyword.length < 2) { setResults([]); setError(''); return }
    const timer = window.setTimeout(() => {
      setLoading(true)
      void api.get('/directory/schools', { params: { q: keyword, limit: 12 } })
        .then((result: unknown) => setResults((result as ApiResult<DirectorySchool[]>).data || []))
        .catch(err => { setResults([]); setError(errorText(err)) })
        .finally(() => setLoading(false))
    }, 260)
    return () => window.clearTimeout(timer)
  }, [query])

  return <div className="directory-picker">
    <label>学校
      <input value={query} onChange={event => { setQuery(event.target.value); onChange(null) }} placeholder="输入学校、省份、城市或办学层次检索" autoComplete="off" required />
    </label>
    {value && <div className="directory-selected"><strong>{value.name}</strong><span>{[value.province, value.city, value.level].filter(Boolean).join(' · ')}</span><button type="button" onClick={() => { setQuery(''); onChange(null) }}>重新选择</button></div>}
    {!value && query.trim().length > 0 && <div className="directory-results">
      {loading && <p>正在检索学校…</p>}
      {!loading && results.map(item => <button type="button" key={item.id} onClick={() => { onChange(item); setQuery(item.name); setResults([]) }}><strong>{item.name}</strong><span>{[item.province, item.city, item.level, item.nature].filter(Boolean).join(' · ')}</span></button>)}
      {!loading && !results.length && !error && query.trim().length >= 2 && <p>未找到学校，可在下方提交补充请求。</p>}
      {error && <p className="error-copy">{error}</p>}
    </div>}
  </div>
}

function DepartmentSelect({ school, value, onChange }: { school: DirectorySchool | null; value: DirectoryDepartment | null; onChange: (value: DirectoryDepartment | null) => void }) {
  const [items, setItems] = useState<DirectoryDepartment[]>([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    onChange(null)
    if (!school) { setItems([]); return }
    setLoading(true)
    void api.get(`/directory/schools/${encodeURIComponent(school.id)}/departments`)
      .then((result: unknown) => setItems((result as ApiResult<DirectoryDepartment[]>).data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [school?.id])
  return <label>申请加入的部门
    <select value={value?.id || ''} disabled={!school || loading} onChange={event => onChange(items.find(item => item.id === event.target.value) || null)} required>
      <option value="">{loading ? '正在加载部门…' : school ? '请选择组织和部门' : '请先选择学校'}</option>
      {items.map(item => <option key={item.id} value={item.id}>{item.displayName}</option>)}
    </select>
    {school && !loading && !items.length && <small className="field-hint">该学校暂未公开可申请的部门。可创建新部门，或联系现有负责人。</small>}
  </label>
}

function CloudOnboarding() {
  const { setUser, setToken, setCsrfToken, setContexts } = useAuthStore()
  const initialEntry = new URLSearchParams(window.location.search).get('entry')
  const [mode, setMode] = useState<'home' | 'login' | 'apply' | 'create'>(
    initialEntry === 'login' || initialEntry === 'apply' || initialEntry === 'create' ? initialEntry : 'home'
  )
  const [school, setSchool] = useState<DirectorySchool | null>(null)
  const [department, setDepartment] = useState<DirectoryDepartment | null>(null)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginSchools, setLoginSchools] = useState<LoginSchoolOption[]>([])
  const [loginSchoolId, setLoginSchoolId] = useState('')
  const [applyForm, setApplyForm] = useState({ studentId: '', fullName: '', faculty: '', className: '', phone: '', email: '', password: '', reason: '' })
  const [createForm, setCreateForm] = useState({ organizationName: '', departmentName: '', studentId: '', fullName: '', faculty: '', className: '', phone: '', email: '', password: '' })
  const [emailVerification, setEmailVerification] = useState({ id: '', code: '', token: '', sentTo: '', verified: false })
  const [allowRegistration, setAllowRegistration] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [humanConfig, setHumanConfig] = useState<HumanVerificationConfig | null>(null)
  const [humanConfigError, setHumanConfigError] = useState('')
  const [loginVerificationRequired, setLoginVerificationRequired] = useState(false)
  const [legalAccepted, setLegalAccepted] = useState({ registration_apply: false, department_create: false })
  const [legalVersions, setLegalVersions] = useState<Record<'registration_apply' | 'department_create', LegalVersions | null>>({
    registration_apply: null,
    department_create: null
  })
  const [turnstileConsent, setTurnstileConsent] = useState<Record<HumanVerificationAction, boolean>>({
    department_create: false,
    registration_apply: false,
    login: false
  })
  const [verificationTokens, setVerificationTokens] = useState<Record<HumanVerificationAction, string>>({
    department_create: '',
    registration_apply: '',
    login: ''
  })
  const [verificationResetKeys, setVerificationResetKeys] = useState<Record<HumanVerificationAction, number>>({
    department_create: 0,
    registration_apply: 0,
    login: 0
  })

  useEffect(() => {
    void api.get('/security/human-verification')
      .then((result: unknown) => {
        setHumanConfig((result as ApiResult<HumanVerificationConfig>).data)
        setHumanConfigError('')
      })
      .catch(() => setHumanConfigError('安全验证配置加载失败，请刷新页面后重试。'))
  }, [])

  useEffect(() => {
    if (!school || !department || mode !== 'apply') { setAllowRegistration(false); return }
    void api.get('/registration/status', { params: { schoolId: school.id, departmentId: department.id } })
      .then((result: unknown) => setAllowRegistration(Boolean((result as ApiResult<{ allowRegistration: boolean }>).data?.allowRegistration)))
      .catch(() => setAllowRegistration(false))
  }, [school?.id, department?.id, mode])

  function resetFeedback(nextMode: 'home' | 'login' | 'apply' | 'create') {
    setMode(nextMode); setError(''); setMessage('')
    if (nextMode !== 'login') { setLoginSchools([]); setLoginSchoolId('') }
  }

  async function sendCreateEmailCode() {
    if (!createForm.email.trim()) { setError('请先填写验证邮箱。'); return }
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post('/auth/email-verifications', { email: createForm.email, purpose: 'department_create' }) as unknown as ApiResult<{ verificationId: string }>
      setEmailVerification({ id: result.data.verificationId, code: '', token: '', sentTo: createForm.email.trim().toLowerCase(), verified: false })
      setMessage('验证码已发送，请在15分钟内完成验证。')
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function confirmCreateEmailCode() {
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post('/auth/email-verifications/confirm', {
        verificationId: emailVerification.id,
        email: createForm.email,
        code: emailVerification.code
      }) as unknown as ApiResult<{ verificationToken: string }>
      setEmailVerification(current => ({ ...current, token: result.data.verificationToken, verified: true }))
      setMessage('邮箱验证完成，可以继续创建部门。')
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  function resetVerification(action: HumanVerificationAction) {
    setVerificationTokens(current => ({ ...current, [action]: '' }))
    setVerificationResetKeys(current => ({ ...current, [action]: current[action] + 1 }))
  }

  function acceptSession(data: { user: User | null; csrfToken?: string; contexts?: DepartmentContext[] }) {
    setToken('cloud-cookie')
    setCsrfToken(data.csrfToken || null)
    setContexts(data.contexts || [])
    setUser(data.user)
  }

  async function submitLogin(event: FormEvent) {
    event.preventDefault()
    if (loginVerificationRequired && humanConfig?.enabled && !turnstileConsent.login) {
      setError('请先单独同意 Cloudflare 人机验证所需的境外处理。')
      return
    }
    if (loginVerificationRequired && humanConfig?.enabled && !verificationTokens.login) {
      setError('请先完成人机验证。')
      return
    }
    setBusy(true); setError('')
    try {
      const result = await api.post('/auth/login', {
        username,
        password,
        turnstileToken: verificationTokens.login,
        turnstileConsent: turnstileConsent.login,
        ...(loginSchoolId ? { schoolId: loginSchoolId } : {})
      }) as unknown as ApiResult<{ user: User | null; csrfToken?: string; contexts?: DepartmentContext[] }>
      acceptSession(result.data)
    } catch (err) {
      const response = err as { code?: string; data?: { schools?: LoginSchoolOption[]; captchaRequired?: boolean } }
      if (response?.code === 'SCHOOL_SELECTION_REQUIRED' && response.data?.schools?.length) {
        setLoginSchools(response.data.schools)
        setLoginSchoolId('')
        setLoginVerificationRequired(false)
      }
      if (response?.code === 'HUMAN_VERIFICATION_REQUIRED' || response.data?.captchaRequired) {
        setLoginVerificationRequired(true)
      }
      setError(errorText(err))
    } finally {
      if (verificationTokens.login) resetVerification('login')
      setBusy(false)
    }
  }

  async function submitApply(event: FormEvent) {
    event.preventDefault()
    if (!humanConfig) { setError('安全验证仍在加载，请稍后再试。'); return }
    if (!legalAccepted.registration_apply || !legalVersions.registration_apply) {
      setError('请先阅读并同意《用户服务协议》和《隐私政策》。')
      return
    }
    if (humanConfig.enabled && !turnstileConsent.registration_apply) {
      setError('请先单独同意 Cloudflare 人机验证所需的境外处理。')
      return
    }
    if (humanConfig.enabled && !verificationTokens.registration_apply) {
      setError('请先完成人机验证。')
      return
    }
    setBusy(true); setError(''); setMessage('')
    try {
      if (!school || !department) throw new Error('请先选择学校和部门。')
      const result = await api.post('/registration/apply', {
        ...applyForm,
        schoolId: school.id,
        departmentId: department.id,
        turnstileToken: verificationTokens.registration_apply,
        turnstileConsent: turnstileConsent.registration_apply,
        legalAccepted: true,
        legalVersions: legalVersions.registration_apply
      }) as unknown as ApiResult<{ requestId: string }>
      setMessage(result.message || '入部申请已提交，请等待部长审批。')
      setApplyForm({ studentId: '', fullName: '', faculty: '', className: '', phone: '', email: '', password: '', reason: '' })
    } catch (err) { setError(errorText(err)) } finally {
      if (humanConfig.enabled) resetVerification('registration_apply')
      setBusy(false)
    }
  }

  async function submitCreate(event: FormEvent) {
    event.preventDefault()
    if (!humanConfig) { setError('安全验证仍在加载，请稍后再试。'); return }
    if (!legalAccepted.department_create || !legalVersions.department_create) {
      setError('请先阅读并同意《用户服务协议》和《隐私政策》。')
      return
    }
    if (humanConfig.enabled && !turnstileConsent.department_create) {
      setError('请先单独同意 Cloudflare 人机验证所需的境外处理。')
      return
    }
    if (humanConfig.enabled && !verificationTokens.department_create) {
      setError('请先完成人机验证。')
      return
    }
    if (!emailVerification.verified || emailVerification.sentTo !== createForm.email.trim().toLowerCase()) {
      setError('请先完成当前邮箱的验证码验证。')
      return
    }
    setBusy(true); setError(''); setMessage('')
    try {
      if (!school) throw new Error('请先选择学校。')
      const result = await api.post('/directory/departments/bootstrap', {
        ...createForm,
        schoolId: school.id,
        emailVerificationToken: emailVerification.token,
        turnstileToken: verificationTokens.department_create,
        turnstileConsent: turnstileConsent.department_create,
        legalAccepted: true,
        legalVersions: legalVersions.department_create
      }) as unknown as ApiResult<{ user: User; csrfToken: string; contexts: DepartmentContext[] }>
      acceptSession(result.data)
    } catch (err) { setError(errorText(err)) } finally {
      if (humanConfig.enabled) resetVerification('department_create')
      setBusy(false)
    }
  }

  return <main className="login-shell"><LoginVisual /><section className={`login-panel cloud-onboarding ${mode === 'home' ? 'cloud-entry-panel' : ''}`}>
    {mode === 'home' && <div className="cloud-entry">
      <div className="cloud-entry-head"><p className="eyebrow">部务云</p><h1>选择进入方式</h1><p>成员用学号登录；新成员先提交入部申请；负责人可为所在学校创建新部门。</p></div>
      <div className="cloud-entry-actions" aria-label="选择登录入口">
        <button type="button" className="cloud-entry-card" onClick={() => resetFeedback('login')}><strong>已有账号登录</strong><span>已加入部门或已被部长建号的成员，从这里进入工作台。</span></button>
        <button type="button" className="cloud-entry-card primary-entry" onClick={() => resetFeedback('apply')}><strong>申请加入部门</strong><span>还没有账号的成员，先选择学校和部门提交申请。</span></button>
        <button type="button" className="cloud-entry-card" onClick={() => resetFeedback('create')}><strong>创建新部门</strong><span>首位负责人开通部门，创建后自动成为部长。</span></button>
      </div>
    </div>}
    {mode !== 'home' && <button type="button" className="login-back" onClick={() => resetFeedback('home')}>返回入口</button>}
    {mode === 'login' && <form className="stack-form" onSubmit={submitLogin} autoComplete="off">
      <div><p className="eyebrow">多部门登录</p><h1>进入部务云</h1><p>使用学号和密码登录。已加入多个部门时，登录后选择要进入的部门。</p></div>
      <label>学号<input value={username} onChange={event => setUsername(event.target.value)} autoComplete="username" required /></label>
      <label>密码<input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" required /></label>
      {loginSchools.length > 0 && <label>确认学校<select value={loginSchoolId} onChange={event => setLoginSchoolId(event.target.value)} required><option value="">请选择你的学校</option>{loginSchools.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select><small className="field-hint">仅当不同学校恰好存在相同学号和密码时才需要确认。</small></label>}
      {loginVerificationRequired && humanConfig?.enabled && <label className="consent-check"><input type="checkbox" checked={turnstileConsent.login} onChange={event => setTurnstileConsent(current => ({ ...current, login: event.target.checked }))} /><span>我单独同意为人机验证向 Cloudflare, Inc. 提供IP地址、设备及浏览器安全信号。<a href="/legal/privacy" target="_blank" rel="noreferrer">查看说明</a></span></label>}
      {loginVerificationRequired && humanConfig?.enabled && turnstileConsent.login && <HumanVerification enabled siteKey={humanConfig.siteKey} action="login" resetKey={verificationResetKeys.login} onChange={token => setVerificationTokens(current => ({ ...current, login: token }))} />}
      {loginVerificationRequired && humanConfigError && <div className="alert error">{humanConfigError}</div>}
      {error && <div className="alert error">{error}</div>}
      <button className="primary wide" disabled={busy || (loginVerificationRequired && (!humanConfig || Boolean(humanConfigError) || (humanConfig.enabled && !verificationTokens.login)))}>{busy ? '正在登录…' : '登录'}</button>
    </form>}
    {mode === 'apply' && <form className="stack-form" onSubmit={submitApply}>
      <div><p className="eyebrow">加入已有部门</p><h1>提交入部申请</h1><p>公开部门只展示所属组织和部门名称；申请通过前不能登录，部长审批后会自动成为普通成员。</p></div>
      <SchoolSearch value={school} onChange={value => { setSchool(value); setDepartment(null) }} />
      <DepartmentSelect school={school} value={department} onChange={setDepartment} />
      {!allowRegistration && school && department && <div className="alert error">该部门暂未开放入部申请，请联系部门负责人。</div>}
      <div className="form-grid compact-form">
        <label>姓名<input value={applyForm.fullName} onChange={event => setApplyForm({ ...applyForm, fullName: event.target.value })} required /></label>
        <label>学号<input value={applyForm.studentId} onChange={event => setApplyForm({ ...applyForm, studentId: event.target.value })} required /></label>
        <label>学院<input value={applyForm.faculty} onChange={event => setApplyForm({ ...applyForm, faculty: event.target.value })} /></label>
        <label>专业/班级<input value={applyForm.className} onChange={event => setApplyForm({ ...applyForm, className: event.target.value })} /></label>
        <label>手机号<input value={applyForm.phone} onChange={event => setApplyForm({ ...applyForm, phone: event.target.value })} /></label>
        <label>邮箱<input type="email" value={applyForm.email} onChange={event => setApplyForm({ ...applyForm, email: event.target.value })} /></label>
        <label className="span-2">设置登录密码<input type="password" minLength={8} value={applyForm.password} onChange={event => setApplyForm({ ...applyForm, password: event.target.value })} autoComplete="new-password" required /></label>
        <label className="span-2">申请说明<textarea value={applyForm.reason} onChange={event => setApplyForm({ ...applyForm, reason: event.target.value })} /></label>
      </div>
      <LegalConsent accepted={legalAccepted.registration_apply} onChange={(accepted, versions) => { setLegalAccepted(current => ({ ...current, registration_apply: accepted })); setLegalVersions(current => ({ ...current, registration_apply: versions })) }} />
      {humanConfig?.enabled && <label className="consent-check"><input type="checkbox" checked={turnstileConsent.registration_apply} onChange={event => setTurnstileConsent(current => ({ ...current, registration_apply: event.target.checked }))} /><span>我单独同意为人机验证向 Cloudflare, Inc. 提供IP地址、设备及浏览器安全信号。拒绝后无法提交公开申请。<a href="/legal/privacy" target="_blank" rel="noreferrer">查看说明</a></span></label>}
      {humanConfig?.enabled && turnstileConsent.registration_apply && <HumanVerification enabled siteKey={humanConfig.siteKey} action="registration_apply" resetKey={verificationResetKeys.registration_apply} onChange={token => setVerificationTokens(current => ({ ...current, registration_apply: token }))} />}
      {humanConfigError && <div className="alert error">{humanConfigError}</div>}
      {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
      <button className="primary wide" disabled={busy || !legalAccepted.registration_apply || !legalVersions.registration_apply || !allowRegistration || !humanConfig || Boolean(humanConfigError) || (humanConfig.enabled && !verificationTokens.registration_apply)}>{busy ? '正在提交…' : '提交入部申请'}</button>
    </form>}
    {mode === 'create' && <form className="stack-form" onSubmit={submitCreate}>
      <div><p className="eyebrow">自助开通</p><h1>创建一个部门</h1><p>创建成功后，你会立即成为该部门的首位部长。每个账号默认只能创建一个有效部门，之后可加入多个部门。</p></div>
      <SchoolSearch value={school} onChange={value => { setSchool(value); setDepartment(null) }} />
      <div className="form-grid compact-form">
        <label>所属组织<input value={createForm.organizationName} onChange={event => setCreateForm({ ...createForm, organizationName: event.target.value })} placeholder="如：校艺术团、学生会" required /></label>
        <label>部门名称<input value={createForm.departmentName} onChange={event => setCreateForm({ ...createForm, departmentName: event.target.value })} placeholder="如：声乐部、广播台" required /></label>
        <label>你的姓名<input value={createForm.fullName} onChange={event => setCreateForm({ ...createForm, fullName: event.target.value })} required /></label>
        <label>你的学号<input value={createForm.studentId} onChange={event => setCreateForm({ ...createForm, studentId: event.target.value })} required /></label>
        <label>学院<input value={createForm.faculty} onChange={event => setCreateForm({ ...createForm, faculty: event.target.value })} /></label>
        <label>专业/班级<input value={createForm.className} onChange={event => setCreateForm({ ...createForm, className: event.target.value })} /></label>
        <label>手机号<input value={createForm.phone} onChange={event => setCreateForm({ ...createForm, phone: event.target.value })} /></label>
        <label>验证邮箱<input type="email" value={createForm.email} onChange={event => { setCreateForm({ ...createForm, email: event.target.value }); setEmailVerification({ id: '', code: '', token: '', sentTo: '', verified: false }) }} required /></label>
        <div className="span-2 email-verification-row">
          {!emailVerification.id && <button type="button" className="secondary" disabled={busy || !createForm.email.trim()} onClick={() => void sendCreateEmailCode()}>发送邮箱验证码</button>}
          {emailVerification.id && !emailVerification.verified && <><label>邮箱验证码<input inputMode="numeric" maxLength={6} value={emailVerification.code} onChange={event => setEmailVerification(current => ({ ...current, code: event.target.value.replace(/\D/g, '').slice(0, 6) }))} /></label><button type="button" className="secondary" disabled={busy || emailVerification.code.length !== 6} onClick={() => void confirmCreateEmailCode()}>确认邮箱</button><button type="button" className="text-btn" disabled={busy} onClick={() => void sendCreateEmailCode()}>重新发送</button></>}
          {emailVerification.verified && <div className="email-verified"><strong>邮箱已验证</strong><span>{emailVerification.sentTo}</span></div>}
        </div>
        <label className="span-2">设置登录密码<input type="password" minLength={8} value={createForm.password} onChange={event => setCreateForm({ ...createForm, password: event.target.value })} autoComplete="new-password" required /></label>
      </div>
      <LegalConsent accepted={legalAccepted.department_create} onChange={(accepted, versions) => { setLegalAccepted(current => ({ ...current, department_create: accepted })); setLegalVersions(current => ({ ...current, department_create: versions })) }} />
      {humanConfig?.enabled && <label className="consent-check"><input type="checkbox" checked={turnstileConsent.department_create} onChange={event => setTurnstileConsent(current => ({ ...current, department_create: event.target.checked }))} /><span>我单独同意为人机验证向 Cloudflare, Inc. 提供IP地址、设备及浏览器安全信号。拒绝后无法提交公开创建申请。<a href="/legal/privacy" target="_blank" rel="noreferrer">查看说明</a></span></label>}
      {humanConfig?.enabled && turnstileConsent.department_create && <HumanVerification enabled siteKey={humanConfig.siteKey} action="department_create" resetKey={verificationResetKeys.department_create} onChange={token => setVerificationTokens(current => ({ ...current, department_create: token }))} />}
      {humanConfigError && <div className="alert error">{humanConfigError}</div>}
      {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
      <button className="primary wide" disabled={busy || !emailVerification.verified || !legalAccepted.department_create || !legalVersions.department_create || !humanConfig || Boolean(humanConfigError) || (humanConfig.enabled && !verificationTokens.department_create)}>{busy ? '正在创建…' : '创建部门并开始30天试用'}</button>
      <button type="button" className="text-action" onClick={() => { const name = window.prompt('请输入未收录学校的完整名称'); if (name) void api.post('/directory/school-requests', { name }).then(() => setMessage('学校补充请求已提交。')).catch(err => setError(errorText(err))) }}>未找到学校？提交补充请求</button>
    </form>}
  </section></main>
}

function Login() {
  const { cloudMode, setCloudMode, setUser, setToken, setCsrfToken, setContexts } = useAuthStore()
  const [modeReady, setModeReady] = useState(false)
  const [mode, setMode] = useState<'login' | 'apply'>('apply')
  const [schoolCode, setSchoolCode] = useState('')
  const [departmentCode, setDepartmentCode] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginFieldsUnlocked, setLoginFieldsUnlocked] = useState(false)
  const loginAccountRef = useRef<HTMLInputElement>(null)
  const loginPasswordRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<{ allowRegistration: boolean; inviteRequired: boolean; departmentName: string; schoolName?: string; departmentCode?: string } | null>(null)
  const [applyForm, setApplyForm] = useState({ studentId: '', fullName: '', faculty: '', className: '', phone: '', email: '', password: '', inviteCode: '', reason: '' })
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    void api.get('/health').then(response => {
      const isCloud = (response as unknown as { mode?: string }).mode === 'cloud'
      setCloudMode(isCloud)
    }).catch(() => setMode('login')).finally(() => setModeReady(true))
  }, [])

  useEffect(() => {
    if (!modeReady) return
    if (cloudMode && (!schoolCode.trim() || !departmentCode.trim())) {
      setStatus(null)
      return
    }
    const query = cloudMode
      ? '?schoolCode=' + encodeURIComponent(schoolCode.trim()) + '&departmentCode=' + encodeURIComponent(departmentCode.trim())
      : ''
    void getData<{ allowRegistration: boolean; inviteRequired: boolean; departmentName: string; schoolName?: string; departmentCode?: string }>('/registration/status' + query)
        .then(result => {
          setStatus(result)
          if (!result.allowRegistration) setMode('login')
        })
        .catch(() => setStatus(null))
  }, [cloudMode, schoolCode, departmentCode])

  useEffect(() => {
    if (mode !== 'login') return

    // A browser may reuse a saved localhost password across different USB trial packages.
    const clearSavedCredentials = () => {
      setUsername('')
      setPassword('')
      if (loginAccountRef.current) loginAccountRef.current.value = ''
      if (loginPasswordRef.current) loginPasswordRef.current.value = ''
    }

    clearSavedCredentials()
    const frame = window.requestAnimationFrame(clearSavedCredentials)
    return () => window.cancelAnimationFrame(frame)
  }, [mode])

  function openLogin() {
    setLoginFieldsUnlocked(false)
    setMode('login')
    setError('')
    setMessage('')
  }

  async function submitLogin(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = await api.post('/auth/login', { schoolCode, username, password }) as unknown as ApiResult<{
        user: User | null
        token: string | null
        csrfToken?: string
        contexts?: DepartmentContext[]
        contextRequired?: boolean
      }>
      setToken(cloudMode ? 'cloud-cookie' : result.data.token)
      setCsrfToken(result.data.csrfToken || null)
      setContexts(result.data.contexts || [])
      setUser(result.data.user)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitApply(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const result = await api.post('/registration/apply', { ...applyForm, schoolCode, departmentCode }) as unknown as ApiResult<{ requestId: string }>
      setMessage(result.message || '入部申请已提交，请等待部长审核。')
      setApplyForm({ studentId: '', fullName: '', faculty: '', className: '', phone: '', email: '', password: '', inviteCode: '', reason: '' })
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  if (!modeReady) return <main className="login-shell"><LoginVisual /><section className="login-panel"><div className="loading">正在加载入口…</div></section></main>

  if (cloudMode) return <CloudOnboarding />

  return <main className="login-shell">
    <LoginVisual />
    <section className="login-panel">
      <div className="login-tabs">
        <button className={mode === 'apply' ? 'active' : ''} onClick={() => { setLoginFieldsUnlocked(false); setMode('apply'); setError(''); setMessage('') }}>申请加入部门</button>
        <button className={mode === 'login' ? 'active' : ''} onClick={openLogin}>已有账号登录</button>
      </div>

      {mode === 'login' ? <form className="stack-form" onSubmit={submitLogin} autoComplete="off">
        {cloudMode && <label>学校代码<input value={schoolCode} onChange={event => setSchoolCode(event.target.value.toLowerCase())} autoComplete="organization" placeholder="例如 liming" required /></label>}
        <div><p className="eyebrow">部门内部登录</p><h1>进入工作台</h1><p>成员使用学号作为账号登录；电脑、手机和平板共用同一套权限。</p></div>
        <label>账号 / 学号<input ref={loginAccountRef} name="department-account" value={username} onChange={event => setUsername(event.target.value)} onFocus={() => setLoginFieldsUnlocked(true)} autoComplete="off" readOnly={!loginFieldsUnlocked} data-lpignore="true" data-1p-ignore="true" required /></label>
        <label>密码<input ref={loginPasswordRef} name="department-password" type="password" value={password} onChange={event => setPassword(event.target.value)} onFocus={() => setLoginFieldsUnlocked(true)} autoComplete="off" readOnly={!loginFieldsUnlocked} data-lpignore="true" data-1p-ignore="true" required /></label>
        {error && <div className="alert error">{error}</div>}
        <button className="primary wide" disabled={busy}>{busy ? '正在登录...' : '登录'}</button>
        {(cloudMode || status?.allowRegistration) && <button type="button" className="text-action" onClick={() => { setLoginFieldsUnlocked(false); setMode('apply'); setError('') }}>还没有账号？申请加入部门</button>}
      </form> : <form className="stack-form" onSubmit={submitApply}>
        <div><p className="eyebrow">入部申请</p><h1>申请加入{status?.departmentName || '当前部门'}</h1><p>申请通过前不能登录；部长审批后账号自动加入普通成员。</p></div>
        {cloudMode && <div className="form-grid compact-form">
          <label>学校代码<input value={schoolCode} onChange={event => setSchoolCode(event.target.value.toLowerCase())} placeholder="例如 lmu" required /></label>
          <label>部门代码<input value={departmentCode} onChange={event => setDepartmentCode(event.target.value.toLowerCase())} placeholder="向部长获取" required /></label>
        </div>}
        {!status?.allowRegistration && <div className="alert error">当前未开放入部申请，请联系部长开放申请入口。</div>}
        <div className="form-grid compact-form">
          <label>姓名<input value={applyForm.fullName} onChange={event => setApplyForm({ ...applyForm, fullName: event.target.value })} required /></label>
          <label>学号<input value={applyForm.studentId} onChange={event => setApplyForm({ ...applyForm, studentId: event.target.value })} required /></label>
          <label>学院<input value={applyForm.faculty} onChange={event => setApplyForm({ ...applyForm, faculty: event.target.value })} /></label>
          <label>专业/班级<input value={applyForm.className} onChange={event => setApplyForm({ ...applyForm, className: event.target.value })} /></label>
          <label>手机号<input value={applyForm.phone} onChange={event => setApplyForm({ ...applyForm, phone: event.target.value })} /></label>
          <label>邮箱<input type="email" value={applyForm.email} onChange={event => setApplyForm({ ...applyForm, email: event.target.value })} /></label>
          <label>登录密码<input name="application-password" type="password" minLength={8} value={applyForm.password} onChange={event => setApplyForm({ ...applyForm, password: event.target.value })} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" required /></label>
          <label>邀请码<input value={applyForm.inviteCode} onChange={event => setApplyForm({ ...applyForm, inviteCode: event.target.value })} placeholder={status?.inviteRequired ? '向部长索取' : '可不填'} /></label>
          <label className="span-2">申请说明<textarea value={applyForm.reason} onChange={event => setApplyForm({ ...applyForm, reason: event.target.value })} /></label>
        </div>
        {message && <div className="alert">{message}</div>}
        {error && <div className="alert error">{error}</div>}
        <button className="primary wide" disabled={busy || !status?.allowRegistration}>{busy ? '正在提交...' : '提交入部申请'}</button>
      </form>}
    </section>
  </main>
}

function DepartmentChooser() {
  const { contexts, setUser, setCsrfToken, logout } = useAuthStore()
  const [busyId, setBusyId] = useState('')
  const [error, setError] = useState('')

  async function choose(context: DepartmentContext) {
    setBusyId(context.departmentId)
    setError('')
    try {
      const result = await api.post('/auth/context', { departmentId: context.departmentId }) as unknown as ApiResult<{ user: User; csrfToken: string }>
      setCsrfToken(result.data.csrfToken)
      setUser(result.data.user)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusyId('')
    }
  }

  return <main className="login-shell"><LoginVisual /><section className="login-panel context-panel">
    <p className="eyebrow">多部门账号</p><h1>选择要进入的部门</h1><p>职位、权限和业务数据按部门分别计算。</p>
    {error && <div className="alert error">{error}</div>}
    <div className="context-list">{contexts.map(context => <button className="context-option" key={context.departmentId} disabled={Boolean(busyId)} onClick={() => void choose(context)}><span><strong>{context.departmentName}</strong><small>{context.schoolName} · {context.memberName}</small></span><i>{busyId === context.departmentId ? '进入中' : '进入'}</i></button>)}</div>
    {!contexts.length && <div className="alert error">当前账号没有可用部门，请联系部门负责人。</div>}
    <button className="text-action" onClick={() => void api.post('/auth/logout').catch(() => undefined).finally(logout)}>退出并返回登录</button>
  </section></main>
}

type LegacyPlatformOverview = {
  schools: Array<{ id: string; name: string; code: string; status: string; departmentCount: number; memberCount: number }>
  departments: Array<{ id: string; school_id: string; name: string; code: string; status: string; memberCount: number; minister: string }>
}

export function LegacyPlatformApp() {
  const { csrfToken, setCsrfToken } = useAuthStore()
  const [authenticated, setAuthenticated] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [credentials, setCredentials] = useState({ username: '', password: '' })
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' })
  const [overview, setOverview] = useState<LegacyPlatformOverview>({ schools: [], departments: [] })
  const [school, setSchool] = useState({ name: '', code: '' })
  const [department, setDepartment] = useState({ schoolId: '', name: '', code: '', ministerName: '', ministerStudentId: '' })
  const [initialMinister, setInitialMinister] = useState({ departmentId: '', departmentName: '', name: '', studentId: '' })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    const result = await api.get('/platform/overview') as unknown as ApiResult<LegacyPlatformOverview>
    setOverview(result.data)
    setAuthenticated(true)
  }

  async function login(event: FormEvent) {
    event.preventDefault(); setError('')
    try {
      const result = await api.post('/platform/auth/login', credentials) as unknown as ApiResult<{ csrfToken: string; admin: { mustChangePassword: boolean } }>
      setCsrfToken(result.data.csrfToken)
      setAuthenticated(true)
      setMustChangePassword(Boolean(result.data.admin.mustChangePassword))
      if (!result.data.admin.mustChangePassword) await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function changePlatformPassword(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    try {
      await api.post('/platform/auth/change-password', passwords)
      setPasswords({ oldPassword: '', newPassword: '' })
      setMustChangePassword(false)
      setMessage('平台管理员密码已更新。')
      await load()
    } catch (err) { setError(errorText(err)) }
  }

  useEffect(() => {
    if (!csrfToken) return
    void api.get('/platform/me').then(response => {
      const result = response as unknown as ApiResult<{ mustChangePassword: boolean }>
      setAuthenticated(true)
      setMustChangePassword(Boolean(result.data.mustChangePassword))
      if (!result.data.mustChangePassword) return load()
    }).catch(() => {
      setAuthenticated(false)
      setCsrfToken(null)
    })
  }, [])

  async function createSchool(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    try {
      await api.post('/platform/schools', school)
      setSchool({ name: '', code: '' }); setMessage('学校已创建。'); await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function createDepartment(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    try {
      const result = await api.post('/platform/departments', department) as unknown as ApiResult<{ initialPassword: string | null; existingSchoolAccount: boolean }>
      setMessage(result.data.existingSchoolAccount ? '部门已创建，部长沿用现有学校账号密码。' : `部门已创建，部长初始密码：${result.data.initialPassword}`)
      setDepartment({ schoolId: department.schoolId, name: '', code: '', ministerName: '', ministerStudentId: '' }); await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function toggleDepartment(item: LegacyPlatformOverview['departments'][number]) {
    const nextStatus = item.status === 'active' ? 'disabled' : 'active'
    const action = nextStatus === 'active' ? '启用' : '停用'
    if (!window.confirm(`确认${action}“${item.name}”？`)) return
    setError(''); setMessage('')
    try {
      await api.post(`/platform/departments/${item.id}/status`, { status: nextStatus })
      setMessage(`部门已${action}。`)
      await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function assignInitialMinister(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    try {
      const result = await api.post(`/platform/departments/${initialMinister.departmentId}/initial-minister`, {
        name: initialMinister.name,
        studentId: initialMinister.studentId
      }) as unknown as ApiResult<{ initialPassword: string | null; existingSchoolAccount: boolean }>
      setMessage(result.data.existingSchoolAccount
        ? `已为“${initialMinister.departmentName}”设置首任部长，部长沿用该学校账号密码。`
        : `已为“${initialMinister.departmentName}”设置首任部长。请立即记录初始密码：${result.data.initialPassword}`)
      setInitialMinister({ departmentId: '', departmentName: '', name: '', studentId: '' })
      await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function logoutPlatform() {
    try { await api.post('/platform/auth/logout') } finally { setAuthenticated(false); setCsrfToken(null) }
  }

  if (!authenticated) return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">平台管理</p><h1>管理学校与部门</h1><form className="stack-form" onSubmit={login}><label>平台账号<input value={credentials.username} onChange={event => setCredentials({ ...credentials, username: event.target.value })} /></label><label>密码<input type="password" value={credentials.password} onChange={event => setCredentials({ ...credentials, password: event.target.value })} /></label>{error && <div className="alert error">{error}</div>}<button className="primary wide">登录</button></form><a className="text-action" href="/">返回成员入口</a></section></main>

  if (mustChangePassword) return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">首次登录保护</p><h1>修改平台初始密码</h1><p>完成改密后才能创建学校和部门。</p><form className="stack-form" onSubmit={changePlatformPassword}><label>当前密码<input type="password" value={passwords.oldPassword} onChange={event => setPasswords({ ...passwords, oldPassword: event.target.value })} required /></label><label>新密码<input type="password" minLength={12} value={passwords.newPassword} onChange={event => setPasswords({ ...passwords, newPassword: event.target.value })} required /></label>{error && <div className="alert error">{error}</div>}<button className="primary wide">保存新密码</button></form><button className="text-action" onClick={() => void logoutPlatform()}>退出</button></section></main>

  return <div className="platform-shell">
    <header className="platform-head">
      <div><p className="eyebrow">校园部门管理系统</p><h1>平台管理</h1></div>
      <div><a className="secondary button-link" href="/">成员入口</a><button className="secondary" onClick={() => void logoutPlatform()}>退出</button></div>
    </header>
    {message && <div className="alert">{message}</div>}
    {error && <div className="alert error">{error}</div>}
    <div className="platform-grid">
      <Section title="创建学校"><form className="stack-form" onSubmit={createSchool}>
        <label>学校名称<input value={school.name} onChange={event => setSchool({ ...school, name: event.target.value })} required /></label>
        <label>学校代码<input value={school.code} onChange={event => setSchool({ ...school, code: event.target.value.toLowerCase() })} pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]" required /></label>
        <button className="primary">创建学校</button>
      </form></Section>
      <Section title="创建部门"><form className="stack-form" onSubmit={createDepartment}>
        <label>学校<select value={department.schoolId} onChange={event => setDepartment({ ...department, schoolId: event.target.value })} required><option value="">请选择</option>{overview.schools.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>部门名称<input value={department.name} onChange={event => setDepartment({ ...department, name: event.target.value })} required /></label>
        <label>部门代码<input value={department.code} onChange={event => setDepartment({ ...department, code: event.target.value.toLowerCase() })} required /></label>
        <label>初始部长姓名<input value={department.ministerName} onChange={event => setDepartment({ ...department, ministerName: event.target.value })} required /></label>
        <label>初始部长学号<input value={department.ministerStudentId} onChange={event => setDepartment({ ...department, ministerStudentId: event.target.value })} required /></label>
        <button className="primary">创建部门</button>
      </form></Section>
    </div>
    <Section title="学校与部门"><div className="table-wrap"><table>
      <thead><tr><th>学校</th><th>部门</th><th>代码</th><th>部长</th><th>成员</th><th>状态</th><th>操作</th></tr></thead>
      <tbody>{overview.departments.map(item => <tr key={item.id}>
        <td>{overview.schools.find(schoolItem => schoolItem.id === item.school_id)?.name || '-'}</td>
        <td>{item.name}</td><td>{item.code}</td><td>{item.minister || '未设置'}</td><td>{item.memberCount}</td>
        <td><Badge value={item.status} /></td>
        <td><div className="row-actions">{!item.minister && <button className="text-btn" onClick={() => setInitialMinister({ departmentId: item.id, departmentName: item.name, name: '', studentId: '' })}>设置首任部长</button>}<button className={item.status === 'active' ? 'text-btn danger-text' : 'text-btn'} onClick={() => void toggleDepartment(item)}>{item.status === 'active' ? '停用' : '启用'}</button></div></td>
      </tr>)}</tbody>
    </table></div></Section>
    {initialMinister.departmentId && <div className="modal"><form className="dialog stack-form" onSubmit={assignInitialMinister}>
      <div className="dialog-head"><div><p className="eyebrow">平台初始化</p><h2>设置首任部长</h2></div><button type="button" className="text-btn" onClick={() => setInitialMinister({ departmentId: '', departmentName: '', name: '', studentId: '' })}>关闭</button></div>
      <p>为“{initialMinister.departmentName}”创建首任部长账号。新账号会生成一次性初始密码，部长首次登录后必须修改。</p>
      <label>部长姓名<input value={initialMinister.name} onChange={event => setInitialMinister({ ...initialMinister, name: event.target.value })} required autoFocus /></label>
      <label>部长学号<input value={initialMinister.studentId} onChange={event => setInitialMinister({ ...initialMinister, studentId: event.target.value })} required /></label>
      <div className="dialog-actions"><button type="button" className="secondary" onClick={() => setInitialMinister({ departmentId: '', departmentName: '', name: '', studentId: '' })}>取消</button><button className="primary">设置部长</button></div>
    </form></div>}
  </div>
}

type PlatformSchool = { id: string; name: string; code: string; departmentCount: number; activeDepartmentCount: number; disabledDepartmentCount: number; memberCount: number }
type PlatformDepartment = {
  id: string; schoolId: string; name: string; displayName: string; organizationName: string; code: string; faculty: string; status: 'active' | 'pending_verification' | 'disabled';
  verificationStatus: 'unverified' | 'pending' | 'verified' | 'rejected'; subscriptionStatus: 'legacy' | 'trial_active' | 'trial_readonly' | 'activation_pending' | 'formal' | 'expired'; trialExpiresAt?: string | null; paidUntil?: string | null; memberLimit?: number; storageQuotaBytes?: number; billingEnabled?: boolean;
  memberCount: number; minister: string; ministerStudentId: string; lastActiveAt: string | null; storageUsedBytes: number; disabledReason: string; disabledAt: string | null; disabledByName: string; dataRetentionUntil?: string | null; dataPurgedAt?: string | null; schoolName?: string; activeAssignments?: number
  verificationRequest?: { id: string; contact: string; note: string; proofName: string; proofAvailable?: boolean; status: string; reviewNote: string; createdAt: string; reviewedAt: string | null } | null
}
type PlatformAdmin = { id: string; username: string; name: string; email: string; emailVerified: boolean; status: 'active' | 'disabled'; lastLoginAt: string | null; createdAt: string | null; createdByName: string; mustChangePassword: boolean }
type PlatformAudit = { id: string; action: string; actor_name: string; target_type: string; target_id: string; details: Record<string, unknown>; ip_address: string; created_at: string }
type PlatformOverview = {
  stats: { schoolCount: number; departmentCount: number; activeDepartmentCount: number; disabledDepartmentCount: number; memberCount: number; recentlyActiveDepartmentCount: number }
  schools: PlatformSchool[]
  departments: PlatformDepartment[]
  mailConfigured: boolean
}

function bytesText(value?: number) {
  const bytes = Number(value || 0)
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function PlatformInviteAccept({ token }: { token: string }) {
  const [invite, setInvite] = useState<{ name: string; email: string; expiresAt: string } | null>(null)
  const [form, setForm] = useState({ username: '', password: '', confirmPassword: '' })
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api.get(`/platform/invitations/${encodeURIComponent(token)}/inspect`).then(response => {
      setInvite((response as unknown as ApiResult<{ name: string; email: string; expiresAt: string }>).data)
    }).catch(err => setError(errorText(err)))
  }, [token])

  async function submit(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    if (form.password !== form.confirmPassword) { setError('两次输入的密码不一致。'); return }
    setBusy(true)
    try {
      await api.post(`/platform/invitations/${encodeURIComponent(token)}/accept`, { username: form.username, password: form.password })
      setMessage('平台管理员账号已创建，请使用新账号登录。')
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">平台管理员邀请</p><h1>设置你的平台账号</h1>{invite && <p>{invite.name} · {invite.email}<br />链接有效至 {dateText(invite.expiresAt)}</p>}{error && <div className="alert error">{error}</div>}{message ? <><div className="alert">{message}</div><a className="primary wide button-link" href="/platform">前往平台登录</a></> : invite && <form className="stack-form" onSubmit={submit}><label>平台账号<input value={form.username} onChange={event => setForm({ ...form, username: event.target.value })} autoComplete="username" required /></label><label>设置密码<input type="password" minLength={12} value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} autoComplete="new-password" required /></label><label>确认密码<input type="password" minLength={12} value={form.confirmPassword} onChange={event => setForm({ ...form, confirmPassword: event.target.value })} autoComplete="new-password" required /></label><button className="primary wide" disabled={busy}>{busy ? '正在创建...' : '完成注册'}</button></form>}<a className="text-action" href="/platform">返回平台登录</a></section></main>
}

function PlatformPasswordReset({ token }: { token: string }) {
  const [reset, setReset] = useState<{ name: string; expiresAt: string } | null>(null)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api.get(`/platform/password-resets/${encodeURIComponent(token)}/inspect`).then(response => {
      setReset((response as unknown as ApiResult<{ name: string; expiresAt: string }>).data)
    }).catch(err => setError(errorText(err)))
  }, [token])

  async function submit(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    if (password !== confirmation) { setError('两次输入的密码不一致。'); return }
    setBusy(true)
    try {
      const result = await api.post('/platform/auth/reset-password', { token, password }) as unknown as ApiResult<unknown>
      setMessage(result.message || '密码已更新，请重新登录。')
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">平台管理员</p><h1>重置密码</h1>{reset && <p>{reset.name}<br />链接有效至 {dateText(reset.expiresAt)}</p>}{error && <div className="alert error">{error}</div>}{message ? <><div className="alert">{message}</div><a className="primary wide button-link" href="/platform">返回平台登录</a></> : reset && <form className="stack-form" onSubmit={submit}><label>新密码<input type="password" minLength={12} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" required /></label><label>确认新密码<input type="password" minLength={12} value={confirmation} onChange={event => setConfirmation(event.target.value)} autoComplete="new-password" required /></label><button className="primary wide" disabled={busy}>{busy ? '正在更新...' : '保存新密码'}</button></form>}<a className="text-action" href="/platform">返回平台登录</a></section></main>
}

function PlatformForgotPassword() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage(''); setBusy(true)
    try {
      const result = await api.post('/platform/auth/forgot-password', { email }) as unknown as ApiResult<unknown>
      setMessage(result.message || '如该邮箱已绑定平台管理员账号，重置链接将发送至该邮箱。')
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">平台管理员</p><h1>找回密码</h1><p>输入已验证邮箱，我们会发送一次性密码重置链接。</p><form className="stack-form" onSubmit={submit}><label>邮箱<input type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" required /></label>{error && <div className="alert error">{error}</div>}{message && <div className="alert">{message}</div>}<button className="primary wide" disabled={busy}>{busy ? '正在发送...' : '发送重置链接'}</button></form><a className="text-action" href="/platform">返回平台登录</a></section></main>
}

function PlatformApp() {
  const query = new URLSearchParams(window.location.search)
  const inviteToken = query.get('invite')
  const resetToken = query.get('reset')
  if (inviteToken) return <PlatformInviteAccept token={inviteToken} />
  if (resetToken) return <PlatformPasswordReset token={resetToken} />
  if (query.get('forgot') === '1') return <PlatformForgotPassword />
  return <PlatformConsole />
}

function PlatformConsole() {
  const { csrfToken, setCsrfToken } = useAuthStore()
  const [authenticated, setAuthenticated] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [currentAdmin, setCurrentAdmin] = useState<PlatformAdmin | null>(null)
  const [credentials, setCredentials] = useState({ username: '', password: '' })
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' })
  const [overview, setOverview] = useState<PlatformOverview>({ stats: { schoolCount: 0, departmentCount: 0, activeDepartmentCount: 0, disabledDepartmentCount: 0, memberCount: 0, recentlyActiveDepartmentCount: 0 }, schools: [], departments: [], mailConfigured: false })
  const [admins, setAdmins] = useState<PlatformAdmin[]>([])
  const [view, setView] = useState<'departments' | 'billing' | 'admins' | 'audit'>('departments')
  const [schoolQuery, setSchoolQuery] = useState('')
  const [selectedSchoolId, setSelectedSchoolId] = useState('')
  const [departmentQuery, setDepartmentQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'unverified' | 'pending' | 'trial_readonly' | 'disabled'>('all')
  const [selectedDepartment, setSelectedDepartment] = useState<PlatformDepartment | null>(null)
  const [selectedCreateSchool, setSelectedCreateSchool] = useState<DirectorySchool | null>(null)
  const [departmentForm, setDepartmentForm] = useState({ name: '', code: '', ministerName: '', ministerStudentId: '' })
  const [identityForm, setIdentityForm] = useState({ organizationName: '', departmentName: '' })
  const [ministerForm, setMinisterForm] = useState({ name: '', studentId: '' })
  const [suspension, setSuspension] = useState<PlatformDepartment | null>(null)
  const [suspensionForm, setSuspensionForm] = useState({ reason: '', confirmationName: '' })
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' })
  const [audits, setAudits] = useState<PlatformAudit[]>([])
  const [auditFilters, setAuditFilters] = useState({ action: '', from: '', to: '' })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function load(includeAdmins = true) {
    const [overviewResponse, adminResponse] = await Promise.all([
      api.get('/platform/overview') as unknown as Promise<ApiResult<PlatformOverview>>,
      includeAdmins ? api.get('/platform/admins') as unknown as Promise<ApiResult<PlatformAdmin[]>> : Promise.resolve(null)
    ])
    setOverview(overviewResponse.data)
    if (adminResponse) setAdmins(adminResponse.data)
    setAuthenticated(true)
  }

  useEffect(() => {
    if (!csrfToken) return
    void api.get('/platform/me').then(response => {
      const result = response as unknown as ApiResult<PlatformAdmin>
      setCurrentAdmin(result.data)
      setAuthenticated(true)
      setMustChangePassword(Boolean(result.data.mustChangePassword))
      if (!result.data.mustChangePassword) return load()
    }).catch(() => { setAuthenticated(false); setCsrfToken(null) })
  }, [])

  async function login(event: FormEvent) {
    event.preventDefault(); setError('')
    try {
      const result = await api.post('/platform/auth/login', credentials) as unknown as ApiResult<{ csrfToken: string; admin: PlatformAdmin }>
      setCsrfToken(result.data.csrfToken)
      setCurrentAdmin(result.data.admin)
      setAuthenticated(true)
      setMustChangePassword(Boolean(result.data.admin.mustChangePassword))
      if (!result.data.admin.mustChangePassword) await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function changePlatformPassword(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    try {
      await api.post('/platform/auth/change-password', passwords)
      setPasswords({ oldPassword: '', newPassword: '' })
      setMustChangePassword(false)
      setMessage('平台管理员密码已更新。')
      await load()
    } catch (err) { setError(errorText(err)) }
  }

  async function logoutPlatform() {
    try { await api.post('/platform/auth/logout') } finally { setAuthenticated(false); setCurrentAdmin(null); setCsrfToken(null) }
  }

  async function selectDepartment(item: PlatformDepartment) {
    setError(''); setSelectedDepartment(item)
    setIdentityForm({ organizationName: item.organizationName || '', departmentName: item.name })
    try {
      const result = await api.get(`/platform/departments/${item.id}`) as unknown as ApiResult<PlatformDepartment>
      setSelectedDepartment(result.data)
      setIdentityForm({ organizationName: result.data.organizationName || '', departmentName: result.data.name })
    } catch (err) { setError(errorText(err)) }
  }

  async function createDepartment(event: FormEvent) {
    event.preventDefault(); setError(''); setMessage('')
    if (!selectedCreateSchool) { setError('请先从学校目录中选择学校。'); return }
    setBusy(true)
    try {
      const result = await api.post('/platform/departments', { schoolId: selectedCreateSchool.id, ...departmentForm }) as unknown as ApiResult<{ initialPassword: string | null; existingSchoolAccount: boolean }>
      setMessage(result.data.existingSchoolAccount ? '部门已创建，首任部长沿用已有学校账号密码。' : `部门已创建。请立即记录部长初始密码：${result.data.initialPassword}`)
      setSelectedCreateSchool(null); setDepartmentForm({ name: '', code: '', ministerName: '', ministerStudentId: '' })
      await load(false)
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function saveIdentity(event: FormEvent) {
    event.preventDefault(); if (!selectedDepartment) return
    setBusy(true); setError(''); setMessage('')
    try {
      await api.put(`/platform/departments/${selectedDepartment.id}/identity`, identityForm)
      setMessage('部门基础资料已更新。'); await load(false); await selectDepartment(selectedDepartment)
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function setMinister(event: FormEvent, mode: 'initial' | 'recover') {
    event.preventDefault(); if (!selectedDepartment) return
    setBusy(true); setError(''); setMessage('')
    try {
      const route = mode === 'initial' ? 'initial-minister' : 'recover-minister'
      const payload = mode === 'initial' ? { name: ministerForm.name, studentId: ministerForm.studentId } : { fullName: ministerForm.name, studentId: ministerForm.studentId }
      const result = await api.post(`/platform/departments/${selectedDepartment.id}/${route}`, payload) as unknown as ApiResult<{ initialPassword: string | null; existingSchoolAccount?: boolean }>
      setMessage(result.data.initialPassword ? `部长账号已处理。请立即记录初始密码：${result.data.initialPassword}` : '部长账号已处理，沿用已有学校账号密码。')
      setMinisterForm({ name: '', studentId: '' }); await load(false); await selectDepartment(selectedDepartment)
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function resetMinister() {
    if (!selectedDepartment || !window.confirm(`确认重置“${selectedDepartment.displayName}”当前部长的登录密码？`)) return
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post(`/platform/departments/${selectedDepartment.id}/reset-minister`) as unknown as ApiResult<{ ministerName: string; studentId: string; temporaryPassword: string }>
      setMessage(`已重置 ${result.data.ministerName}（${result.data.studentId}）的部长账号。请立即记录临时密码：${result.data.temporaryPassword}`)
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function submitSuspension(event: FormEvent) {
    event.preventDefault(); if (!suspension) return
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/platform/departments/${suspension.id}/status`, { status: 'disabled', ...suspensionForm })
      setMessage(`部门“${suspension.displayName}”已停用，成员会话已撤销。`); setSuspension(null); setSuspensionForm({ reason: '', confirmationName: '' }); setSelectedDepartment(null); await load(false)
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function enableDepartment(item: PlatformDepartment) {
    if (!window.confirm(`确认启用“${item.displayName}”？`)) return
    setBusy(true); setError(''); setMessage('')
    try { await api.post(`/platform/departments/${item.id}/status`, { status: 'active' }); setMessage(`部门“${item.displayName}”已启用。`); await load(false); await selectDepartment(item) } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function reviewDepartmentVerification(action: 'approve' | 'reject') {
    const request = selectedDepartment?.verificationRequest
    if (!selectedDepartment || !request) return
    const reviewNote = action === 'reject'
      ? window.prompt('请填写驳回原因，部门会立即停止访问。') || ''
      : window.prompt('可填写核验备注（可留空）。') || ''
    if (action === 'reject' && reviewNote.trim().length < 3) {
      setError('驳回原因至少需要3个字符。')
      return
    }
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/platform/department-verifications/${request.id}/review`, { action, reviewNote })
      setMessage(action === 'approve' ? '部门核验已通过。' : '部门核验已驳回。')
      await load(false)
      setSelectedDepartment(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function inviteAdmin(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post('/platform/admin-invitations', inviteForm) as unknown as ApiResult<{ expiresAt: string }>
      setMessage(`邀请邮件已发送，有效至 ${dateText(result.data.expiresAt)}。`); setInviteForm({ name: '', email: '' })
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function setAdminStatus(item: PlatformAdmin) {
    const next = item.status === 'active' ? 'disabled' : 'active'
    if (!window.confirm(`确认${next === 'active' ? '启用' : '停用'}平台管理员“${item.name || item.username}”？`)) return
    setBusy(true); setError(''); setMessage('')
    try { await api.patch(`/platform/admins/${item.id}/status`, { status: next }); setMessage('平台管理员状态已更新。'); await load() } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }

  async function loadAudits(event?: FormEvent) {
    event?.preventDefault(); setError('')
    try {
      const params = new URLSearchParams(Object.entries(auditFilters).filter(([, value]) => value))
      const result = await api.get(`/platform/audit-logs?${params.toString()}`) as unknown as ApiResult<PlatformAudit[]>
      setAudits(result.data)
    } catch (err) { setError(errorText(err)) }
  }

  const filteredSchools = overview.schools.filter(item => item.name.includes(schoolQuery.trim()) || item.code.includes(schoolQuery.trim().toLowerCase()))
  const displayedDepartments = overview.departments.filter(item => {
    if (selectedSchoolId && item.schoolId !== selectedSchoolId) return false
    if (departmentQuery.trim() && !`${item.displayName}${item.code}${item.minister}`.toLowerCase().includes(departmentQuery.trim().toLowerCase())) return false
    if (statusFilter === 'all') return true
    if (statusFilter === 'active' || statusFilter === 'disabled') return item.status === statusFilter
    if (statusFilter === 'unverified' || statusFilter === 'pending') return item.verificationStatus === statusFilter
    return item.subscriptionStatus === statusFilter
  })

  if (!authenticated) return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">部务云平台</p><h1>平台管理员登录</h1><p>管理学校下的部门、平台账号与部长级救援。</p><form className="stack-form" onSubmit={login}><label>平台账号<input value={credentials.username} onChange={event => setCredentials({ ...credentials, username: event.target.value })} autoComplete="username" required /></label><label>密码<input type="password" value={credentials.password} onChange={event => setCredentials({ ...credentials, password: event.target.value })} autoComplete="current-password" required /></label>{error && <div className="alert error">{error}</div>}<button className="primary wide">登录平台</button></form><a className="text-action" href="/platform?forgot=1">忘记密码</a><a className="text-action" href="/">返回成员入口</a></section></main>

  if (mustChangePassword) return <main className="login-shell"><LoginVisual /><section className="login-panel"><p className="eyebrow">首次登录保护</p><h1>修改平台初始密码</h1><p>完成改密后才能使用平台控制台。</p><form className="stack-form" onSubmit={changePlatformPassword}><label>当前密码<input type="password" value={passwords.oldPassword} onChange={event => setPasswords({ ...passwords, oldPassword: event.target.value })} required /></label><label>新密码<input type="password" minLength={12} value={passwords.newPassword} onChange={event => setPasswords({ ...passwords, newPassword: event.target.value })} required /></label>{error && <div className="alert error">{error}</div>}<button className="primary wide">保存新密码</button></form><button className="text-action" onClick={() => void logoutPlatform()}>退出</button></section></main>

  return <main className="platform-console">
    <header className="platform-console-head"><div><p className="eyebrow">部务云平台运营控制台</p><h1>学校与部门管理</h1><p>当前管理员：{currentAdmin?.name || currentAdmin?.username}</p></div><div className="row-actions"><a className="secondary button-link" href="/">成员入口</a><button className="secondary" onClick={() => void logoutPlatform()}>退出</button></div></header>
    {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
    {selectedDepartment?.verificationStatus === 'pending' && selectedDepartment.verificationRequest && <section className="platform-verification-review">
      <div><strong>部门核验待处理</strong><span>{selectedDepartment.displayName}</span></div>
      <p>联系方式：{selectedDepartment.verificationRequest.contact || '未填写'}<br />说明：{selectedDepartment.verificationRequest.note || '未填写'}<br />证明：{selectedDepartment.verificationRequest.proofAvailable ? <a href={`/api/platform/department-verifications/${selectedDepartment.verificationRequest.id}/proof`} target="_blank" rel="noreferrer">{selectedDepartment.verificationRequest.proofName || '查看脱敏证明'}</a> : '未上传'}</p>
      <div className="row-actions"><button className="primary" disabled={busy} onClick={() => void reviewDepartmentVerification('approve')}>通过核验</button><button className="danger" disabled={busy} onClick={() => void reviewDepartmentVerification('reject')}>驳回</button></div>
    </section>}
    <section className="platform-metrics"><div><span>学校</span><strong>{overview.stats.schoolCount}</strong></div><div><span>部门</span><strong>{overview.stats.departmentCount}</strong></div><div><span>启用部门</span><strong>{overview.stats.activeDepartmentCount}</strong></div><div><span>停用部门</span><strong>{overview.stats.disabledDepartmentCount}</strong></div><div><span>成员</span><strong>{overview.stats.memberCount}</strong></div><div><span>近 7 天登录部门</span><strong>{overview.stats.recentlyActiveDepartmentCount}</strong></div></section>
    <nav className="platform-tabs"><button className={view === 'departments' ? 'active' : ''} onClick={() => setView('departments')}>组织管理</button><button className={view === 'billing' ? 'active' : ''} onClick={() => setView('billing')}>开通与退款</button><button className={view === 'admins' ? 'active' : ''} onClick={() => setView('admins')}>平台管理员</button><button className={view === 'audit' ? 'active' : ''} onClick={() => { setView('audit'); void loadAudits() }}>审计记录</button></nav>
    {view === 'billing' && <PlatformBilling />}
    {view === 'departments' && <><div className="platform-workspace"><aside className="school-tree"><div className="school-tree-head"><strong>学校</strong><input value={schoolQuery} onChange={event => setSchoolQuery(event.target.value)} placeholder="搜索学校" /></div><button className={!selectedSchoolId ? 'school-tree-item active' : 'school-tree-item'} onClick={() => setSelectedSchoolId('')}><span>全部学校</span><small>{overview.stats.departmentCount} 个部门</small></button>{filteredSchools.map(item => <button key={item.id} className={selectedSchoolId === item.id ? 'school-tree-item active' : 'school-tree-item'} onClick={() => setSelectedSchoolId(item.id)}><span>{item.name}</span><small>{item.activeDepartmentCount} 启用 · {item.disabledDepartmentCount} 停用</small></button>)}</aside><section className="platform-department-panel"><div className="platform-panel-head"><div><h2>{selectedSchoolId ? overview.schools.find(item => item.id === selectedSchoolId)?.name || '学校部门' : '全部部门'}</h2><p>学校仅用于分类；部门停用会立即中止该部门所有成员访问。</p></div><div className="platform-filters"><input value={departmentQuery} onChange={event => setDepartmentQuery(event.target.value)} placeholder="搜索部门、代码或部长" /><select value={statusFilter} onChange={event => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">全部状态</option><option value="active">已启用</option><option value="unverified">未认证</option><option value="pending">待核验</option><option value="trial_readonly">试用到期</option><option value="disabled">已停用</option></select></div></div><div className="table-wrap"><table><thead><tr><th>部门</th><th>部长</th><th>成员</th><th>最近登录</th><th>存储</th><th>状态</th><th></th></tr></thead><tbody>{displayedDepartments.map(item => <tr key={item.id}><td><strong>{item.displayName}</strong><small>{item.code}</small></td><td>{item.minister || '未设置'}</td><td>{item.memberCount}</td><td>{dateText(item.lastActiveAt)}</td><td>{bytesText(item.storageUsedBytes)}</td><td><div className="department-state-stack"><Badge value={item.status} /><Badge value={item.verificationStatus} /><Badge value={item.subscriptionStatus} /></div></td><td><button className="text-btn" onClick={() => void selectDepartment(item)}>管理</button></td></tr>)}{!displayedDepartments.length && <tr><td colSpan={7}><Empty text="没有符合条件的部门" /></td></tr>}</tbody></table></div></section></div><Section title="创建新部门" className="platform-create-department"><form className="stack-form" onSubmit={createDepartment}><SchoolSearch value={selectedCreateSchool} onChange={setSelectedCreateSchool} /><label>部门名称<input value={departmentForm.name} onChange={event => setDepartmentForm({ ...departmentForm, name: event.target.value })} required /></label><label>部门代码<input value={departmentForm.code} onChange={event => setDepartmentForm({ ...departmentForm, code: event.target.value.toLowerCase() })} pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]" required /></label><label>首任部长姓名<input value={departmentForm.ministerName} onChange={event => setDepartmentForm({ ...departmentForm, ministerName: event.target.value })} required /></label><label>首任部长学号<input value={departmentForm.ministerStudentId} onChange={event => setDepartmentForm({ ...departmentForm, ministerStudentId: event.target.value })} required /></label><button className="primary" disabled={busy}>创建部门</button></form></Section></>}
    {view === 'admins' && <div className="platform-admin-grid"><Section title="邀请平台管理员"><form className="stack-form" onSubmit={inviteAdmin}><p>{overview.mailConfigured ? '邀请链接将发送到受邀者邮箱，24 小时内有效且只能使用一次。' : 'QQ 邮箱 SMTP 尚未配置，暂时不能发出邀请。'}</p><label>姓名<input value={inviteForm.name} onChange={event => setInviteForm({ ...inviteForm, name: event.target.value })} required /></label><label>邮箱<input type="email" value={inviteForm.email} onChange={event => setInviteForm({ ...inviteForm, email: event.target.value })} required /></label><button className="primary" disabled={busy || !overview.mailConfigured}>发送邀请</button></form></Section><Section title="有效平台管理员"><div className="table-wrap"><table><thead><tr><th>姓名</th><th>账号</th><th>邮箱</th><th>最近登录</th><th>状态</th><th></th></tr></thead><tbody>{admins.map(item => <tr key={item.id}><td>{item.name || '-'}</td><td>{item.username}</td><td>{item.email || '未绑定'}</td><td>{dateText(item.lastLoginAt)}</td><td><Badge value={item.status} /></td><td>{item.id !== currentAdmin?.id && <button className={item.status === 'active' ? 'text-btn danger-text' : 'text-btn'} onClick={() => void setAdminStatus(item)}>{item.status === 'active' ? '停用' : '启用'}</button>}</td></tr>)}</tbody></table></div></Section></div>}
    {view === 'audit' && <Section title="平台审计记录"><form className="platform-audit-filter" onSubmit={loadAudits}><input value={auditFilters.action} onChange={event => setAuditFilters({ ...auditFilters, action: event.target.value })} placeholder="操作类型，例如 department_status_changed" /><input type="date" value={auditFilters.from} onChange={event => setAuditFilters({ ...auditFilters, from: event.target.value })} /><input type="date" value={auditFilters.to} onChange={event => setAuditFilters({ ...auditFilters, to: event.target.value })} /><button className="secondary">查询</button></form><div className="table-wrap"><table><thead><tr><th>时间</th><th>操作</th><th>操作者</th><th>对象</th><th>来源 IP</th></tr></thead><tbody>{audits.map(item => <tr key={item.id}><td>{dateText(item.created_at)}</td><td>{item.action}</td><td>{item.actor_name || '-'}</td><td>{item.target_type} · {item.target_id}</td><td>{item.ip_address || '-'}</td></tr>)}{!audits.length && <tr><td colSpan={5}><Empty text="暂无匹配的审计记录" /></td></tr>}</tbody></table></div></Section>}
    {selectedDepartment && <aside className="platform-drawer"><div className="platform-drawer-head"><div><p className="eyebrow">部门运营管理</p><h2>{selectedDepartment.displayName}</h2><p>{selectedDepartment.schoolName || overview.schools.find(item => item.id === selectedDepartment.schoolId)?.name}</p></div><button className="text-btn" onClick={() => setSelectedDepartment(null)}>关闭</button></div><div className="platform-drawer-facts"><div><span>成员</span><strong>{selectedDepartment.memberCount}</strong></div><div><span>有效任职</span><strong>{selectedDepartment.activeAssignments ?? '-'}</strong></div><div><span>最近登录</span><strong>{dateText(selectedDepartment.lastActiveAt)}</strong></div><div><span>存储</span><strong>{bytesText(selectedDepartment.storageUsedBytes)}</strong></div></div>{selectedDepartment.status === 'disabled' && <div className="alert error">已停用：{selectedDepartment.disabledReason || '未填写原因'}<br />{selectedDepartment.dataPurgedAt ? `数据已于 ${dateText(selectedDepartment.dataPurgedAt)} 按保存期限清理` : `可在 ${dateText(selectedDepartment.dataRetentionUntil)} 前恢复或导出`}<br />{dateText(selectedDepartment.disabledAt)} {selectedDepartment.disabledByName ? `· ${selectedDepartment.disabledByName}` : ''}</div>}<Section title="启用与停用"><p>停用会立即撤销成员会话。数据封存30天供恢复或导出，期满后清理可删除业务数据；依法必要的财务、审计和争议记录限制保存。</p>{selectedDepartment.status === 'active' ? <button className="danger" disabled={busy} onClick={() => { setSuspension(selectedDepartment); setSuspensionForm({ reason: '', confirmationName: '' }) }}>停用部门</button> : selectedDepartment.status === 'disabled' && !selectedDepartment.dataPurgedAt ? <button className="primary" disabled={busy} onClick={() => void enableDepartment(selectedDepartment)}>重新启用部门</button> : selectedDepartment.status === 'pending_verification' ? <div className="alert">待核验部门只能通过核验审批启用。</div> : <div className="alert error">数据已经按保存期限清理，不能直接恢复。</div>}</Section><Section title="部门基础资料"><form className="stack-form" onSubmit={saveIdentity}><label>所属组织<input value={identityForm.organizationName} onChange={event => setIdentityForm({ ...identityForm, organizationName: event.target.value })} placeholder="例如 校艺术团" required /></label><label>部门名称<input value={identityForm.departmentName} onChange={event => setIdentityForm({ ...identityForm, departmentName: event.target.value })} required /></label><button className="secondary" disabled={busy}>保存资料</button></form></Section><Section title="部长级救援"><p>当前部长：{selectedDepartment.minister || '未设置'} {selectedDepartment.ministerStudentId ? `（${selectedDepartment.ministerStudentId}）` : ''}</p>{selectedDepartment.minister ? <><button className="secondary" disabled={busy} onClick={() => void resetMinister()}>重置部长密码</button><form className="stack-form platform-compact-form" onSubmit={event => void setMinister(event, 'recover')}><strong>紧急更换部长</strong><label>新部长姓名<input value={ministerForm.name} onChange={event => setMinisterForm({ ...ministerForm, name: event.target.value })} required /></label><label>新部长学号<input value={ministerForm.studentId} onChange={event => setMinisterForm({ ...ministerForm, studentId: event.target.value })} required /></label><button className="secondary" disabled={busy}>执行部长更换</button></form></> : <form className="stack-form" onSubmit={event => void setMinister(event, 'initial')}><label>首任部长姓名<input value={ministerForm.name} onChange={event => setMinisterForm({ ...ministerForm, name: event.target.value })} required /></label><label>首任部长学号<input value={ministerForm.studentId} onChange={event => setMinisterForm({ ...ministerForm, studentId: event.target.value })} required /></label><button className="primary" disabled={busy}>设置首任部长</button></form>}</Section></aside>}
    {suspension && <div className="modal"><form className="dialog stack-form" onSubmit={submitSuspension}><div className="dialog-head"><div><p className="eyebrow">高风险操作</p><h2>停用部门</h2></div><button type="button" className="text-btn" onClick={() => setSuspension(null)}>关闭</button></div><p>停用“{suspension.displayName}”会立即撤销全部成员会话。部门数据封存30天供恢复或导出，期满后将清理可删除业务数据；必要的财务、审计和争议记录限制保存。</p><label>停用原因<textarea value={suspensionForm.reason} onChange={event => setSuspensionForm({ ...suspensionForm, reason: event.target.value })} minLength={3} maxLength={300} required /></label><label>输入完整部门名称确认<input value={suspensionForm.confirmationName} onChange={event => setSuspensionForm({ ...suspensionForm, confirmationName: event.target.value })} placeholder={suspension.displayName} required /></label><div className="dialog-actions"><button type="button" className="secondary" onClick={() => setSuspension(null)}>取消</button><button className="danger" disabled={busy}>确认停用</button></div></form></div>}
  </main>
}

function PasswordGate({ user, onDone, onExit }: { user: User; onDone: (next: User) => void; onExit: () => void }) {
  const { cloudMode, setToken } = useAuthStore()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = await api.post('/auth/change-password', { oldPassword, newPassword }) as unknown as ApiResult<{ user: User; token: string }>
      if (!cloudMode) setToken(result.data.token)
      onDone(result.data.user)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return <div className="modal"><form className="dialog" onSubmit={submit}><p className="eyebrow">首次登录保护</p><h2>请先修改初始密码</h2><p>当前账号：{user.fullName}（{user.studentId || user.username}）</p><label>当前密码<input type="password" value={oldPassword} onChange={event => setOldPassword(event.target.value)} autoComplete="current-password" /></label><label>新密码<input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} minLength={8} autoComplete="new-password" /></label>{error && <div className="alert error">{error}</div>}<div className="password-gate-actions"><button type="button" className="secondary" disabled={busy} onClick={onExit}>退出并返回登录</button><button className="primary" disabled={busy}>{busy ? '正在保存...' : '保存新密码'}</button></div></form></div>
}

function ProfileDialog({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: (next: User) => void }) {
  const { cloudMode, setToken } = useAuthStore()
  const [form, setForm] = useState({
    fullName: user.fullName || '',
    email: user.email || '',
    faculty: user.faculty || '',
    className: user.className || '',
    phone: user.phone || '',
    notes: user.notes || '',
    phoneVisibility: user.phoneVisibility || 'managers',
    emailVisibility: user.emailVisibility || 'managers'
  })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const result = await api.put('/auth/profile', form) as unknown as ApiResult<{ user: User; token: string }>
      if (!cloudMode) setToken(result.data.token)
      onSaved(result.data.user)
      setMessage(result.message || '个人资料已保存')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return <div className="modal">
    <form className="dialog profile-dialog" onSubmit={submit}>
      <div className="dialog-head"><div><p className="eyebrow">个人资料</p><h2>设置我的信息</h2></div><button type="button" className="text-btn" onClick={onClose}>关闭</button></div>
      <div className="profile-summary">
        <span>登录账号：{user.username}</span>
        <span>学号：{user.studentId || '未设置'}</span>
        <span>身份：{roleLabel[user.role] || user.role}</span>
      </div>
      <div className="form-grid compact-form">
        <label>姓名<input value={form.fullName} onChange={event => setForm({ ...form, fullName: event.target.value })} required /></label>
        <label>邮箱<input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} /></label>
        <label>学院<input value={form.faculty} onChange={event => setForm({ ...form, faculty: event.target.value })} /></label>
        <label>专业/班级<input value={form.className} onChange={event => setForm({ ...form, className: event.target.value })} /></label>
        <label>手机号<input value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} /></label>
        <label>手机号公开范围<select value={form.phoneVisibility} onChange={event => setForm({ ...form, phoneVisibility: event.target.value as 'department' | 'managers' })}><option value="managers">仅管理者可见</option><option value="department">部门成员可见</option></select></label>
        <label>邮箱公开范围<select value={form.emailVisibility} onChange={event => setForm({ ...form, emailVisibility: event.target.value as 'department' | 'managers' })}><option value="managers">仅管理者可见</option><option value="department">部门成员可见</option></select></label>
        <label className="span-2">备注<textarea value={form.notes} maxLength={500} onChange={event => setForm({ ...form, notes: event.target.value })} /></label>
      </div>
      {message && <div className="alert">{message}</div>}
      {error && <div className="alert error">{error}</div>}
      {cloudMode && <AccountDeletionPanel onChanged={onClose} />}
      <div className="form-actions"><button type="submit" className="primary" disabled={busy}>{busy ? '正在保存...' : '保存资料'}</button></div>
    </form>
  </div>
}

function Overview({ data, go }: { data: Dashboard; go: (tab: Tab) => void }) {
  const metrics = [
    ['我的待办', data.metrics.myOpenTasks, 'tasks'],
    ['待审批', data.metrics.pendingApprovals, 'approvals'],
    ['今日排班', data.metrics.todaySchedule, 'schedule'],
    ['近期活动', data.metrics.upcomingActivities, 'activities']
  ] as const
  return <>
    <div className="welcome"><div><p className="eyebrow">{data.department.faculty || '校园组织'}</p><h1>{data.department.name}</h1><p>{data.user.roleName} · 今天有 {data.metrics.myOpenTasks} 项待办</p></div><div className="live"><i />{data.system.storage === 'mariadb' ? '云端服务正常' : '局域网服务正常'}</div></div>
    <div className="metric-grid">{metrics.map(([label, value, tab]) => <button key={label} className="metric" onClick={() => go(tab)}><span>{label}</span><strong>{value}</strong><small>查看详情</small></button>)}</div>
    <div className="dashboard-grid">
      <Section title="我的任务" action={<button className="text-btn" onClick={() => go('tasks')}>全部任务</button>}>
        <div className="compact-list">{data.tasks.mine.length ? data.tasks.mine.slice(0, 5).map(task => <div className="compact-row" key={task.id}><div><strong>{task.title}</strong><small>{task.assignee_name || '待分配'} · 截止 {dateText(task.due_date, { defaultToday: true })}</small></div><Badge value={task.status} /></div>) : <Empty text="暂时没有待处理任务" />}</div>
      </Section>
      <Section title="今日排班">
        <div className="compact-list">{data.schedule.today.length ? data.schedule.today.map((item, index) => <div className="compact-row" key={`${item.member_id || index}`}><div><strong>{item.member_name || item.name || '值班成员'}</strong><small>{item.period_name || item.period || item.location || '部门值班'}</small></div><span className="time">{item.time || item.start_time || `第 ${item.period_id || index + 1} 节`}</span></div>) : <Empty text="今天没有排班" />}</div>
      </Section>
      <Section title="财务摘要">
        <div className="finance-strip"><div><span>收入</span><strong className="income">{money(data.finance.totalIncome)}</strong></div><div><span>支出</span><strong className="expense">{money(data.finance.totalExpense)}</strong></div><div><span>起始</span><strong>{financialStartText(data.finance)}</strong></div><div><span>结余</span><strong>{money(data.finance.netIncome)}</strong></div></div>
        <button className="secondary wide" onClick={() => go('finance')}>查看财务明细</button>
      </Section>
      <Section title="部门公告">
        <div className="notice-list">{data.notices.length ? data.notices.map(notice => <article key={notice.id}><div><strong>{notice.title}</strong><small>{notice.author_name || '部门'} · {dateText(notice.created_at)}</small></div><p>{notice.content}</p></article>) : <Empty text="暂时没有公告" />}</div>
      </Section>
    </div>
  </>
}

function Tasks({ data, users, canManage, reload }: { data: Task[]; users: User[]; canManage: boolean; reload: () => Promise<void> }) {
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(() => ({ title: '', description: '', assigneeId: '', priority: 'medium', dueDate: todayDateTime() }))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function create(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    const user = users.find(item => item.id === form.assigneeId)
    try {
      await api.post('/tasks', { ...form, assigneeName: user?.fullName || '' })
      setForm({ title: '', description: '', assigneeId: '', priority: 'medium', dueDate: todayDateTime() })
      setShowForm(false)
      await reload()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function act(path: string, body: any = {}) {
    setError('')
    try {
      await api.post(path, body)
      await reload()
    } catch (err) {
      setError(errorText(err))
    }
  }

  function requestChange(task: Task) {
    const reason = window.prompt('请填写延期原因或期望完成时间')
    if (reason) void act(`/tasks/${task.id}/requests`, { type: 'extension', reason })
  }

  return <div className="page-stack">
    <div className="page-title"><div><p className="eyebrow">执行协同</p><h1>任务中心</h1><p>分配、确认、提交进度和异常申请形成完整记录。</p></div>{canManage && <button className="primary" onClick={() => setShowForm(value => !value)}>{showForm ? '收起' : '新建任务'}</button>}</div>
    {showForm && <Section title="分配任务"><form className="form-grid" onSubmit={create}><label>任务名称<input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} required /></label><label>执行人<select value={form.assigneeId} onChange={event => setForm({ ...form, assigneeId: event.target.value })}><option value="">待分配</option>{users.map(user => <option key={user.id} value={user.id}>{user.fullName}</option>)}</select></label><label>优先级<select value={form.priority} onChange={event => setForm({ ...form, priority: event.target.value })}><option value="low">普通</option><option value="medium">重要</option><option value="high">紧急</option></select></label><label>截止时间<input type="datetime-local" value={form.dueDate} onChange={event => setForm({ ...form, dueDate: event.target.value })} /></label><label className="span-2">任务说明<textarea value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></label><div className="form-actions span-2"><button className="primary" disabled={busy}>确认分配</button></div></form></Section>}
    {error && <div className="alert error">{error}</div>}
    <div className="task-board">{['pending', 'in_progress', 'completed'].map(status => <Section key={status} title={`${statusLabel[status]} · ${data.filter(task => task.status === status).length}`}><div className="task-column">{data.filter(task => task.status === status).map(task => <article className="task-item" key={task.id}><div className="task-line"><Badge value={task.priority} /><small>{task.assignee_name || '待分配'}</small></div><h3>{task.title}</h3><p>{task.description || '暂无说明'}</p><div className="progress"><i style={{ width: `${task.progress || 0}%` }} /></div><footer><small>{task.progress || 0}% · {dateText(task.due_date, { defaultToday: true })}</small><div>{task.status === 'pending' && <button onClick={() => void act(`/tasks/${task.id}/acknowledge`)}>确认收到</button>}{task.status !== 'completed' && <button onClick={() => void act(`/tasks/${task.id}/progress`, { progress: Math.min(100, Number(task.progress || 0) + 25) })}>推进 25%</button>}{task.status !== 'completed' && <button onClick={() => requestChange(task)}>申请延期</button>}</div></footer></article>)}{!data.some(task => task.status === status) && <Empty text={`没有${statusLabel[status]}的任务`} />}</div></Section>)}</div>
  </div>
}

function Schedule({ dashboard, users, activities, reload }: { dashboard: Dashboard; users: User[]; activities: Activity[]; reload: () => Promise<void> }) {
  return <ScheduleWorkspace dashboard={dashboard} users={users} activities={activities} onChanged={reload} />
}

function Activities({ data, users, canManage, reload }: { data: Activity[]; users: User[]; canManage: boolean; reload: () => Promise<void> }) {
  const [selectedId, setSelectedId] = useState('')
  const [detail, setDetail] = useState<ActivityDetail | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [form, setForm] = useState(() => ({
    name: '',
    type: 'team_building',
    startDate: todayDate(),
    endDate: todayDate(),
    location: '',
    signupEnabled: true,
    signupLimit: '',
    signupDeadline: '',
    feeEnabled: false,
    feeAmount: '',
    budget: '',
    description: ''
  }))
  const [taskForm, setTaskForm] = useState(() => ({ title: '', assigneeId: '', participantScope: 'registered_only' as 'registered_only' | 'department_members', priority: 'medium', dueDate: todayDateTime(), description: '' }))
  const typeLabel: Record<string, string> = { team_building: '团建活动', work: '工作活动', other: '其他活动' }
  const scopeLabel: Record<string, string> = { registered_only: '参与者任务', department_members: '工作人员任务' }
  const manager = detail?.canManage || canManage
  const currentActivity = detail?.activity || data.find(item => item.id === selectedId) || null
  const defaultScope = (item?: Activity | null) => item?.type === 'team_building' ? 'registered_only' : 'department_members'

  useEffect(() => {
    if (!selectedId && data.length) setSelectedId(data[0].id)
    if (selectedId && !data.some(item => item.id === selectedId)) setSelectedId(data[0]?.id || '')
  }, [data, selectedId])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    void loadDetail(selectedId)
  }, [selectedId])

  useEffect(() => {
    if (!currentActivity) return
    setTaskForm(prev => ({ ...prev, assigneeId: '', participantScope: defaultScope(currentActivity) }))
  }, [currentActivity?.id, currentActivity?.type])

  async function loadDetail(id = selectedId) {
    if (!id) return
    setError('')
    try {
      const next = await getData<ActivityDetail>(`/activities/${id}`)
      setDetail(next)
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const result = await action()
      const maybe = result as ApiResult<Activity | ActivityDetail>
      if (maybe?.data && 'id' in maybe.data) setSelectedId(String((maybe.data as Activity).id))
      setMessage(success)
      await reload()
      await loadDetail()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault()
    await run(async () => {
      const result = await api.post('/activities', {
        ...form,
        signupLimit: form.signupLimit ? Number(form.signupLimit) : '',
        feeAmount: form.feeAmount ? Number(form.feeAmount) : 0,
        budget: form.budget ? Number(form.budget) : 0
      }) as unknown as ApiResult<Activity>
      setForm({ name: '', type: 'team_building', startDate: todayDate(), endDate: todayDate(), location: '', signupEnabled: true, signupLimit: '', signupDeadline: '', feeEnabled: false, feeAmount: '', budget: '', description: '' })
      setShowForm(false)
      setSelectedId(result.data.id)
      return result
    }, '活动已创建。')
  }

  function selectedActivity() {
    return detail?.activity || data.find(item => item.id === selectedId) || null
  }

  async function signup() {
    if (!selectedId) return
    await run(() => api.post(`/activities/${selectedId}/signup`), '报名成功。')
  }

  async function cancelSignup() {
    if (!selectedId) return
    await run(() => api.post(`/activities/${selectedId}/cancel-signup`), '已取消报名。')
  }

  async function requestExit() {
    if (!selectedId) return
    const reason = window.prompt('请填写退出原因，提交后等待活动负责人审批。')
    if (!reason) return
    await run(() => api.post(`/activities/${selectedId}/exit-requests`, { reason }), '退出申请已提交。')
  }

  async function reviewExit(requestId: string, decision: 'approved' | 'rejected') {
    if (!selectedId) return
    const note = window.prompt(decision === 'approved' ? '审批备注，可不填' : '请填写驳回原因，可不填') || ''
    await run(() => api.post(`/activities/${selectedId}/exit-requests/${requestId}/review`, { decision, note }), decision === 'approved' ? '已同意退出申请。' : '已驳回退出申请。')
  }

  async function updateFee(record: ActivityFeeRecord) {
    if (!selectedId) return
    const due = window.prompt('应缴金额', String(record.dueAmount))
    if (due === null) return
    const paid = window.prompt('已缴金额', String(record.paidAmount))
    if (paid === null) return
    const note = window.prompt('备注，可不填', record.note || '') || ''
    await run(() => api.put(`/activities/${selectedId}/fees/${record.id}`, { dueAmount: Number(due), paidAmount: Number(paid), note }), '费用记录已更新。')
  }

  async function createTask(event: FormEvent) {
    event.preventDefault()
    if (!selectedId) return
    const user = users.find(item => item.id === taskForm.assigneeId)
    await run(() => api.post(`/activities/${selectedId}/tasks`, { ...taskForm, assigneeName: user?.fullName || '' }), '活动任务已创建。')
    setTaskForm({ title: '', assigneeId: '', participantScope: defaultScope(currentActivity), priority: 'medium', dueDate: todayDateTime(), description: '' })
  }

  async function deleteActivity() {
    const activity = selectedActivity()
    if (!selectedId || !activity) return
    if (!window.confirm(`确认删除活动「${activity.name}」？报名、费用和退出申请会一起删除，关联任务和排班会保留但解除活动关联。`)) return
    setBusy(true)
    setError('')
    setMessage('')
    try {
      await api.delete(`/activities/${selectedId}`)
      const nextId = data.find(item => item.id !== selectedId)?.id || ''
      setSelectedId(nextId)
      setDetail(null)
      setMessage('活动已删除。')
      await reload()
      if (nextId) await loadDetail(nextId)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const activity = selectedActivity()
  const activeRegistrationOptions = detail?.registrations.filter(item => item.status === 'active').map(item => ({ id: item.userId, fullName: item.userName })) || []
  const taskAssigneeOptions = taskForm.participantScope === 'registered_only' ? activeRegistrationOptions : users
  const registered = Boolean(detail?.myRegistration && detail.myRegistration.status === 'active')
  const canSelfCancel = Boolean(detail?.myRegistration?.canSelfCancel)

  return <div className="page-stack">
    <div className="page-title"><div><p className="eyebrow">活动协同</p><h1>活动中心</h1><p>团建可报名和收款，工作活动可关联任务与排班。</p></div>{canManage && <button className="primary" onClick={() => setShowForm(value => !value)}>{showForm ? '收起' : '新建活动'}</button>}</div>
    {showForm && <Section title="创建活动"><form className="form-grid" onSubmit={create}>
      <label>活动名称<input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} required /></label>
      <label>活动类型<select value={form.type} onChange={event => setForm({ ...form, type: event.target.value })}><option value="team_building">团建活动</option><option value="work">工作活动</option><option value="other">其他活动</option></select></label>
      <label>开始日期<input type="date" value={form.startDate} onChange={event => setForm({ ...form, startDate: event.target.value })} required /></label>
      <label>结束日期<input type="date" value={form.endDate} onChange={event => setForm({ ...form, endDate: event.target.value })} required /></label>
      <label>地点<input value={form.location} onChange={event => setForm({ ...form, location: event.target.value })} /></label>
      <label>预算<input type="number" min="0" step="0.01" value={form.budget} onChange={event => setForm({ ...form, budget: event.target.value })} /></label>
      <label className="toggle-row"><input type="checkbox" checked={form.signupEnabled} onChange={event => setForm({ ...form, signupEnabled: event.target.checked })} />开放报名</label>
      <label>报名上限<input type="number" min="0" placeholder="不填则不限" value={form.signupLimit} onChange={event => setForm({ ...form, signupLimit: event.target.value })} /></label>
      <label>报名截止<input type="datetime-local" value={form.signupDeadline} onChange={event => setForm({ ...form, signupDeadline: event.target.value })} /></label>
      <label className="toggle-row"><input type="checkbox" checked={form.feeEnabled} onChange={event => setForm({ ...form, feeEnabled: event.target.checked })} />启用活动小账本</label>
      <label>人均应缴<input type="number" min="0" step="0.01" value={form.feeAmount} onChange={event => setForm({ ...form, feeAmount: event.target.value })} /></label>
      <label className="span-2">活动说明<textarea value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></label>
      <div className="form-actions span-2"><button className="primary" disabled={busy}>保存活动</button></div>
    </form></Section>}
    {message && <div className="alert">{message}</div>}
    {error && <div className="alert error">{error}</div>}
    <div className="activity-workspace">
      <div className="activity-list">{data.map(item => <button type="button" className={item.id === selectedId ? 'activity-item active' : 'activity-item'} key={item.id} onClick={() => setSelectedId(item.id)}>
        <div className="activity-top"><Badge value={item.status} /><span>{typeLabel[item.type || 'team_building']}</span></div>
        <h2>{item.name}</h2>
        <p>{item.description || '暂无活动说明'}</p>
        <dl><div><dt>报名</dt><dd>{item.registrationCount || 0}{item.signupLimit ? `/${item.signupLimit}` : ' 人'}</dd></div><div><dt>任务</dt><dd>{item.openTaskCount || 0}/{item.taskCount || 0}</dd></div><div><dt>排班</dt><dd>{item.schedulePlanCount || 0} 个</dd></div></dl>
      </button>)}{!data.length && <Empty text="暂时没有活动" />}</div>
      <section className="panel activity-detail">
        {!activity && <Empty text="请选择一个活动" />}
        {activity && <>
          <header className="activity-detail-head"><div><p className="eyebrow">{typeLabel[activity.type || 'team_building']}</p><h2>{activity.name}</h2><p>{activity.location || '地点待定'} · {dateText(activity.startDate || activity.start_date, { dateOnly: true, defaultToday: true })}</p></div><Badge value={activity.status} /></header>
          <div className="activity-actions">
            {!manager && !registered && activity.signupEnabled !== false && <button className="primary" disabled={busy} onClick={() => void signup()}>报名参加</button>}
            {!manager && registered && canSelfCancel && <button className="secondary" disabled={busy} onClick={() => void cancelSignup()}>取消报名</button>}
            {!manager && registered && !canSelfCancel && <button className="secondary" disabled={busy} onClick={() => void requestExit()}>申请退出</button>}
            {manager && <button className="danger" disabled={busy} onClick={() => void deleteActivity()}>删除活动</button>}
            {registered && <span className="helper">你已报名该活动。</span>}
          </div>
          <div className="activity-metrics">
            <div><span>已报名</span><strong>{activity.registrationCount || detail?.registrations.filter(item => item.status === 'active').length || 0}</strong></div>
            <div><span>活动小账本</span><strong>{money(activity.feeSummary?.paidAmount)}</strong></div>
            <div><span>未缴金额</span><strong>{money(activity.feeSummary?.unpaidAmount)}</strong></div>
            <div><span>关联任务</span><strong>{activity.taskCount || detail?.tasks.length || 0}</strong></div>
          </div>
          <p className="activity-description">{activity.description || '暂无活动说明。'}</p>
          <div className="activity-sections">
            <section><h3>报名名单</h3><div className="compact-list">{detail?.registrations.length ? detail.registrations.map(item => <div className="compact-row" key={item.id}><div><strong>{item.userName}</strong><small>{dateText(item.createdAt)} 报名</small></div><Badge value={item.status} /></div>) : <Empty text="暂无报名成员" />}</div></section>
            <section><h3>工作人员</h3><div className="compact-list">{detail?.workers.length ? detail.workers.map(worker => <div className="compact-row" key={worker.userId}><div><strong>{worker.userName}</strong><small>由活动任务自动纳入 · {worker.taskCount} 项任务</small></div><Badge value="in_progress" /></div>) : <Empty text="暂无未报名工作人员" />}</div></section>
            <section><h3>费用小账本</h3><div className="table-wrap compact-table"><table><thead><tr><th>成员</th><th>应缴</th><th>已缴</th><th>状态</th><th>备注</th><th>操作</th></tr></thead><tbody>{detail?.feeRecords.map(record => <tr key={record.id}><td>{record.userName}</td><td>{money(record.dueAmount)}</td><td>{money(record.paidAmount)}</td><td><Badge value={record.status} /></td><td>{record.note || '-'}</td><td>{manager ? <button className="text-btn" onClick={() => void updateFee(record)}>登记</button> : '-'}</td></tr>)}</tbody></table>{!detail?.feeRecords.length && <Empty text={activity.feeEnabled ? '暂无费用记录' : '该活动未启用小账本'} />}</div></section>
            <section><h3>活动任务</h3>{manager && <form className="activity-task-form" onSubmit={createTask}><input placeholder="任务名称" value={taskForm.title} onChange={event => setTaskForm({ ...taskForm, title: event.target.value })} required /><select value={taskForm.participantScope} onChange={event => setTaskForm({ ...taskForm, participantScope: event.target.value as 'registered_only' | 'department_members', assigneeId: '' })}><option value="registered_only">仅报名成员</option><option value="department_members">部门成员</option></select><select value={taskForm.assigneeId} onChange={event => setTaskForm({ ...taskForm, assigneeId: event.target.value })}><option value="">待分配</option>{taskAssigneeOptions.map(user => <option key={user.id} value={user.id}>{user.fullName}</option>)}</select><input type="datetime-local" value={taskForm.dueDate} onChange={event => setTaskForm({ ...taskForm, dueDate: event.target.value })} /><button className="secondary" disabled={busy}>添加任务</button></form>}<div className="compact-list">{detail?.tasks.length ? detail.tasks.map(task => <div className="compact-row" key={task.id}><div><strong>{task.title}</strong><small>{scopeLabel[task.activity_participant_scope || 'department_members']} · {task.assignee_name || '待分配'} · {dateText(task.due_date, { defaultToday: true })}</small></div><Badge value={task.status} /></div>) : <Empty text="暂无关联任务" />}</div></section>
            <section><h3>关联排班</h3><div className="compact-list">{detail?.schedulePlans.length ? detail.schedulePlans.map(plan => <div className="compact-row" key={plan.id}><div><strong>{plan.name}</strong><small>{plan.location || '地点待定'} · {plan.shiftCount} 个班次</small></div><Badge value={plan.status} /></div>) : <Empty text="暂无关联排班计划" />}</div></section>
            {manager && <section><h3>退出申请</h3><div className="compact-list">{detail?.exitRequests.length ? detail.exitRequests.map(request => <div className="compact-row" key={request.id}><div><strong>{request.user_name}</strong><small>{request.reason || '未填写原因'} · {dateText(request.created_at)}</small></div><div className="row-actions"><Badge value={request.status} />{request.status === 'pending' && <><button className="text-btn" onClick={() => void reviewExit(request.id, 'approved')}>同意</button><button className="text-btn" onClick={() => void reviewExit(request.id, 'rejected')}>驳回</button></>}</div></div>) : <Empty text="暂无退出申请" />}</div></section>}
          </div>
        </>}
      </section>
    </div>
  </div>
}

function Finance({ dashboard, data, activities = dashboard.activities, users = [], canWrite, reload }: {
  dashboard: Dashboard
  data: Transaction[]
  activities?: Activity[]
  users?: User[]
  canWrite: boolean
  reload: () => Promise<void>
}) {
  const newForm = () => ({
    title: '',
    amount: '',
    type: 'expense',
    category: '日常支出',
    date: todayDate(),
    description: '',
    activityId: '',
    counterpartyUserId: '',
    proofStatus: 'provided',
    proofNote: '',
    requestId: crypto.randomUUID()
  })
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(newForm)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function create(event: FormEvent) {
    event.preventDefault()
    setError('')
    setMessage('')
    try {
      const result = await api.post('/finance', form) as unknown as ApiResult<Transaction>
      setMessage(result.message || (result.data.approvalStatus === 'pending' ? '记录已提交审批。' : '记录已保存。'))
      setForm(newForm())
      setShowForm(false)
      await reload()
    } catch (err) {
      setError(errorText(err))
    }
  }

  return <div className="page-stack">
    <div className="page-title">
      <div>
        <p className="eyebrow">部门账本</p>
        <h1>财务</h1>
        <p>预算内的日常收支直接记账；超预算、超额度、本人报销或暂缺凭证时才进入审批。</p>
      </div>
      {canWrite && <button className="primary" onClick={() => setShowForm(value => !value)}>{showForm ? '收起' : '登记收支'}</button>}
    </div>
    <div className="finance-metrics">
      <div><span>累计收入</span><strong className="income">{money(dashboard.finance.totalIncome)}</strong></div>
      <div><span>已确认支出</span><strong className="expense">{money(dashboard.finance.totalExpense)}</strong></div>
      <div><span>待审批占用</span><strong>{money(dashboard.finance.pendingExpense || 0)}</strong></div>
      <div><span>当前结余</span><strong>{money(dashboard.finance.netIncome)}</strong></div>
      <div><span>账本起始日期</span><strong>{financialStartText(dashboard.finance)}</strong></div>
    </div>
    {showForm && <Section title="登记一笔收支">
      <form className="form-grid" onSubmit={create}>
        <label>事项<input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} required /></label>
        <label>金额<input type="number" min="0.01" step="0.01" value={form.amount} onChange={event => setForm({ ...form, amount: event.target.value })} required /></label>
        <label>类型<select value={form.type} onChange={event => setForm({ ...form, type: event.target.value })}><option value="expense">支出</option><option value="income">收入</option></select></label>
        <label>分类<input value={form.category} onChange={event => setForm({ ...form, category: event.target.value })} required /></label>
        <label>日期<input type="date" value={form.date} onChange={event => setForm({ ...form, date: event.target.value })} required /></label>
        <label>关联活动<select value={form.activityId} onChange={event => setForm({ ...form, activityId: event.target.value })}><option value="">不关联活动</option>{activities.map(activity => <option value={activity.id} key={activity.id}>{activity.name}</option>)}</select></label>
        <label>经手/报销对象<select value={form.counterpartyUserId} onChange={event => setForm({ ...form, counterpartyUserId: event.target.value })}><option value="">未指定</option><option value={dashboard.user.id}>本人（登记人）</option>{users.filter(member => member.status !== 'disabled' && member.status !== 'departed').map(member => <option value={member.id} key={member.id}>{member.fullName} {member.studentId ? '· ' + member.studentId : ''}</option>)}</select></label>
        <label>凭证情况<select value={form.proofStatus} onChange={event => setForm({ ...form, proofStatus: event.target.value })}><option value="provided">已留存凭证</option><option value="explained">已说明凭证情况</option><option value="missing">暂缺凭证，提交审批</option><option value="not_required">无需凭证</option></select></label>
        <label className="span-2">备注<input value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></label>
        <label className="span-2">凭证说明<input value={form.proofNote} onChange={event => setForm({ ...form, proofNote: event.target.value })} placeholder="例如：票据已归档、后续补交原因" /></label>
        <div className="form-actions span-2"><button className="primary">保存记录</button></div>
      </form>
    </Section>}
    {message && <div className="alert">{message}</div>}
    {error && <div className="alert error">{error}</div>}
    <Section title="收支明细">
      <div className="table-wrap">
        <table>
          <thead><tr><th>日期</th><th>事项</th><th>分类</th><th>状态</th><th>审批原因</th><th>金额</th></tr></thead>
          <tbody>{data.map(item => <tr key={item.id}>
            <td>{dateText(item.date, { dateOnly: true, defaultToday: true })}</td>
            <td>{item.title}</td>
            <td>{item.category}</td>
            <td><Badge value={item.approvalStatus} /></td>
            <td>{item.approvalReasonText?.join('；') || item.reviewNote || '—'}</td>
            <td className={item.type}>{item.type === 'income' ? '+' : '-'}{money(item.amount)}</td>
          </tr>)}</tbody>
        </table>
        {!data.length && <Empty text="还没有收支记录" />}
      </div>
    </Section>
  </div>
}
function MemberDetail({ memberId, governance, onBack, reload }: { memberId: string; governance: Governance | null; onBack: () => void; reload: () => Promise<void> }) {
  const [profile, setProfile] = useState<MemberProfile | null>(null)
  const [edit, setEdit] = useState({ fullName: '', email: '', phone: '', faculty: '', className: '', joinedAt: '', notes: '' })
  const [appointment, setAppointment] = useState({ requestedRole: 'deputy', reason: '' })
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const data = await getData<MemberProfile>(`/users/${memberId}/profile`)
      setProfile(data)
      const activeRoles = data.assignments.filter(item => item.status === 'active').map(item => item.role)
      const nextRole = Object.keys(governance?.templates || {}).find(key => key !== 'member' && !activeRoles.includes(key)) || 'deputy'
      setAppointment(current => activeRoles.includes(current.requestedRole) || current.requestedRole === 'member' ? { ...current, requestedRole: nextRole } : current)
      setEdit({
        fullName: data.member.fullName || '', email: data.member.email || '', phone: data.member.phone || '',
        faculty: data.member.faculty || '', className: data.member.className || '', joinedAt: data.member.joinedAt || '', notes: data.member.notes || ''
      })
    } catch (err) {
      setError(errorText(err))
    }
  }

  useEffect(() => { void load() }, [memberId])

  async function run(action: () => Promise<unknown>, success: string | ((result: unknown) => string)) {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      const result = await action()
      setMessage(typeof success === 'function' ? success(result) : success)
      await reload()
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    await run(() => api.put(`/users/${memberId}/profile`, edit), '成员档案已保存。')
  }

  async function appoint(event: FormEvent) {
    event.preventDefault()
    await run(
      () => api.post('/governance/appointment-requests', { targetUserId: memberId, ...appointment }),
      (result) => {
        const response = result as ApiResult<Appointment>
        setAppointment({ requestedRole: 'deputy', reason: '' })
        return response.message || (response.data.status === 'approved' ? '任命已生效。' : '任命申请已提交，请等待部长或有效代理审批。')
      }
    )
  }

  async function resetPassword() {
    if (!window.confirm('确认重置该成员密码？新密码只显示一次。')) return
    setBusy(true)
    setError('')
    try {
      const result = await api.post(`/users/${memberId}/reset-password`) as unknown as ApiResult<{ temporaryPassword: string }>
      setMessage(`临时密码：${result.data.temporaryPassword}。请立即交给成员，关闭页面后不会再次显示。`)
      await reload()
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function disableMember() {
    const reason = window.prompt('请输入停用原因（可选）；取消则不操作。')
    if (reason === null) return
    await run(() => api.post(`/users/${memberId}/disable`, { reason }), '成员账号已停用。')
  }

  async function departMember() {
    const reason = window.prompt('请输入离部原因（必填）；取消则不操作。')
    if (reason === null) return
    if (!reason.trim()) { setError('办理离部必须填写原因。'); return }
    await run(() => api.post(`/users/${memberId}/depart`, { reason }), '成员已转入历史成员，原业务记录继续保留。')
  }

  async function restoreMember() {
    if (!window.confirm('确认将该成员恢复为在职普通成员？原职位不会自动恢复。')) return
    await run(() => api.post(`/users/${memberId}/restore`), '成员已恢复为在职普通成员。')
  }

  async function endAssignment(assignment: Assignment) {
    const reason = window.prompt(`请输入解除“${assignment.role_name}”任职的原因；取消则不操作。`)
    if (reason === null) return
    if (!reason.trim()) { setError('解除任职必须填写原因。'); return }
    await run(() => api.post(`/governance/assignments/${assignment.id}/end`, { reason }), '任职已解除，相关权限已重新计算。')
  }

  if (!profile) return <div className="page-stack"><button className="text-btn back-action" onClick={onBack}>返回成员名单</button>{error ? <div className="alert error">{error}</div> : <div className="loading">正在加载成员详情...</div>}</div>
  const member = profile.member
  const canOperate = profile.canManage && !profile.isSelf && member.role !== 'admin'
  const activeAssignments = profile.assignments.filter(item => item.status === 'active')
  const selectedTemplate = governance?.templates[appointment.requestedRole]
  const availableTemplates = Object.entries(governance?.templates || {}).filter(([key]) => key !== 'member' && !activeAssignments.some(item => item.role === key))

  return <div className="page-stack member-detail-page">
    <div className="page-title member-detail-head"><div><button className="text-btn back-action" onClick={onBack}>返回成员名单</button><p className="eyebrow">成员档案</p><h1>{member.fullName}</h1><div className="role-tags">{activeAssignments.length ? activeAssignments.map(item => <span className={`role-tag role-${item.role}`} key={item.id}>{item.role_name}</span>) : <span className="role-tag">普通成员</span>}</div><p>{member.faculty || '未填学院'} · {member.className || '未填班级'}</p></div><Badge value={member.status || 'active'} /></div>
    {message && <div className="alert">{message}</div>}
    {error && <div className="alert error">{error}</div>}

    <div className="member-profile-grid">
      <Section title="基本资料">
        <dl className="profile-facts">
          <div><dt>姓名</dt><dd>{member.fullName}</dd></div><div><dt>当前任职</dt><dd>{activeAssignments.map(item => item.role_name).join('、') || '普通成员'}</dd></div>
          <div><dt>学院</dt><dd>{member.faculty || '-'}</dd></div><div><dt>专业/班级</dt><dd>{member.className || '-'}</dd></div>
          <div><dt>手机号</dt><dd>{member.phone || '未公开'}</dd></div><div><dt>邮箱</dt><dd>{member.email || '未公开'}</dd></div>
          {member.studentId !== undefined && <div><dt>学号/账号</dt><dd>{member.studentId || member.username || '-'}</dd></div>}
          {member.joinedAt !== undefined && <div><dt>入部时间</dt><dd>{member.joinedAt || '-'}</dd></div>}
          {member.departedAt && <div><dt>离部时间</dt><dd>{dateText(member.departedAt)}</dd></div>}
          {member.departureReason && <div><dt>离部原因</dt><dd>{member.departureReason}</dd></div>}
        </dl>
      </Section>
      <Section title="工作摘要">
        {profile.workSummary ? <div className="work-summary"><div><strong>{profile.workSummary.openTaskCount}</strong><span>未完成任务</span></div><div><strong>{profile.workSummary.completedTaskCount}</strong><span>已完成任务</span></div><div><strong>{profile.workSummary.scheduleCount}</strong><span>排班记录</span></div></div> : <Empty text="工作摘要仅本人和管理者可见" />}
      </Section>
    </div>

    {profile.canManage && <Section title="管理档案">
      <form className="form-grid" onSubmit={save}>
        <label>姓名<input value={edit.fullName} onChange={event => setEdit({ ...edit, fullName: event.target.value })} required /></label>
        <label>入部时间<input type="date" value={edit.joinedAt} onChange={event => setEdit({ ...edit, joinedAt: event.target.value })} /></label>
        <label>学院<input value={edit.faculty} onChange={event => setEdit({ ...edit, faculty: event.target.value })} /></label>
        <label>专业/班级<input value={edit.className} onChange={event => setEdit({ ...edit, className: event.target.value })} /></label>
        <label>手机号<input value={edit.phone} onChange={event => setEdit({ ...edit, phone: event.target.value })} /></label>
        <label>邮箱<input type="email" value={edit.email} onChange={event => setEdit({ ...edit, email: event.target.value })} /></label>
        <label className="span-2">管理备注<textarea value={edit.notes} maxLength={500} onChange={event => setEdit({ ...edit, notes: event.target.value })} /></label>
        <div className="form-actions span-2"><button className="primary" disabled={busy}>保存档案</button></div>
      </form>
    </Section>}

    {profile.canManage && governance && member.status === 'active' && <Section title="新增任职">
      {availableTemplates.length ? <form className="form-grid" onSubmit={appoint}><label>拟任职位<select value={appointment.requestedRole} onChange={event => setAppointment({ ...appointment, requestedRole: event.target.value })}>{availableTemplates.map(([key, item]) => <option value={key} key={key}>{item.name}</option>)}</select></label><label className="span-2">任命说明<textarea value={appointment.reason} onChange={event => setAppointment({ ...appointment, reason: event.target.value })} required /></label>{selectedTemplate && <div className="position-preview span-2"><strong>{selectedTemplate.name}</strong><p>{selectedTemplate.description}</p><div className="permission-tags">{selectedTemplate.permissions.map(permission => <span className={governance.sensitivePermissions.includes(permission) ? 'sensitive' : ''} key={permission}>{permissionLabel[permission] || permission}</span>)}</div><small>{selectedTemplate.category === 'minister' ? '部长全系唯一，生效后会自动结束原部长任职。' : selectedTemplate.category === 'deputy' ? '副部长允许多人任职，但不能与部长由同一人兼任。' : '职能岗位可以与管理岗及其他职能岗位兼任。'}</small></div>}<div className="form-actions span-2"><button className="primary" disabled={busy}>提交任命申请</button></div></form> : <p className="helper">该成员已经拥有全部可任命职位。</p>}
    </Section>}

    <Section title="任职记录"><div className="table-wrap"><table><thead><tr><th>职位</th><th>类型</th><th>状态</th><th>开始时间</th><th>结束时间</th>{profile.canManage && <th>操作</th>}</tr></thead><tbody>{profile.assignments.map(item => <tr key={item.id}><td>{item.role_name || roleLabel[item.role] || item.role}</td><td>{item.category === 'minister' ? '部长' : item.category === 'deputy' ? '管理岗' : item.category === 'functional' ? '职能岗' : '基础身份'}</td><td><Badge value={item.status} /></td><td>{dateText(item.starts_at)}</td><td>{item.ends_at ? dateText(item.ends_at) : '至今'}</td>{profile.canManage && <td>{item.status === 'active' && item.role !== 'admin' ? <button className="text-btn danger-text" disabled={busy} onClick={() => void endAssignment(item)}>解除</button> : '-'}</td>}</tr>)}</tbody></table>{!profile.assignments.length && <Empty text="暂无任职记录" />}</div></Section>

    {profile.canManage && profile.auditLogs.length > 0 && <Section title="成员操作记录"><div className="compact-list">{profile.auditLogs.map(item => <div className="compact-row" key={item.id}><div><strong>{item.summary}</strong><small>{item.actor_name || item.user_name || '系统'} · {dateText(item.created_at)}</small></div></div>)}</div></Section>}

    {profile.canManage && <Section title="账号与离部管理"><div className="member-danger-actions">{member.status === 'active' ? <>{canOperate && <button className="secondary" disabled={busy} onClick={() => void resetPassword()}>重置临时密码</button>}{canOperate && <button className="danger" disabled={busy} onClick={() => void disableMember()}>停用账号</button>}{canOperate && <button className="danger" disabled={busy} onClick={() => void departMember()}>办理离部</button>}{!canOperate && <p className="helper">不能在此操作本人或部长账号。</p>}</> : <button className="primary" disabled={busy} onClick={() => void restoreMember()}>恢复为在职成员</button>}</div></Section>}
  </div>
}

function Members({ members, governance, canManage, reload }: { members: User[]; governance: Governance | null; canManage: boolean; reload: () => Promise<void> }) {
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [registrationRequests, setRegistrationRequests] = useState<RegistrationRequest[]>([])
  const [settings, setSettings] = useState({ allowRegistration: false, inviteCode: '' })
  const [directory, setDirectory] = useState<User[]>(members)
  const [directoryStatus, setDirectoryStatus] = useState<'active' | 'departed' | 'disabled'>('active')
  const [query, setQuery] = useState('')
  const [role, setRole] = useState('all')
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  async function loadRegistration() {
    if (!canManage) return
    try {
      const [requests, nextSettings] = await Promise.all([
        getData<RegistrationRequest[]>('/registration/requests'),
        getData<{ allowRegistration: boolean; inviteCode: string }>('/registration/settings')
      ])
      setRegistrationRequests(requests)
      setSettings(nextSettings)
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  async function loadDirectory(status = directoryStatus) {
    try {
      setDirectory(await getData<User[]>(`/users/directory?status=${status}`))
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  useEffect(() => { void loadRegistration() }, [canManage])
  useEffect(() => { if (directoryStatus === 'active') setDirectory(members); else void loadDirectory(directoryStatus) }, [members, directoryStatus])

  async function downloadTemplate() {
    try {
      const blob = await api.get('/users/import-template', { responseType: 'blob' }) as unknown as Blob
      downloadBlob(blob, '成员导入模板.xlsx')
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  async function importMembers(event: FormEvent) {
    event.preventDefault()
    if (!importFile) return
    setMessage('')
    setImportResult(null)
    try {
      const result = await api.post('/users/batch-import', await importFile.arrayBuffer(), {
        headers: { 'Content-Type': importFile.type || 'application/octet-stream' }
      }) as unknown as ApiResult<ImportResult>
      setImportResult(result.data)
      setMessage(`导入完成：创建 ${result.data.createdCount} 人，跳过 ${result.data.skippedCount} 行。`)
      await reload()
      await loadDirectory('active')
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  function downloadDistribution() {
    if (!importResult?.created.length) return
    const rows = [
      ['姓名', '学号/账号', '初始密码', '登录地址', '学院', '专业/班级', '邮箱', '手机号'],
      ...importResult.created.map(user => [user.fullName, user.studentId || user.username, user.initialPassword, window.location.origin, user.faculty || '', user.className || '', user.email || '', user.phone || ''])
    ]
    const csv = '\uFEFF' + rows.map(row => row.map(csvEscape).join(',')).join('\n')
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `账号分发清单_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  async function saveRegistrationSettings(event: FormEvent) {
    event.preventDefault()
    setMessage('')
    try {
      await api.put('/registration/settings', settings)
      setMessage('入部申请设置已保存。')
      await loadRegistration()
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  async function reviewRegistration(id: string, action: 'approve' | 'reject') {
    try {
      const reason = action === 'reject' ? window.prompt('请输入驳回原因') || '' : ''
      await api.post(`/registration/review/${id}`, { action, reason })
      await loadRegistration()
      await reload()
      await loadDirectory(directoryStatus)
    } catch (err) {
      setMessage(errorText(err))
    }
  }

  const filtered = directory.filter(member => {
    const keyword = query.trim().toLowerCase()
    const matchesQuery = !keyword || [member.fullName, member.studentId, member.username, member.faculty, member.className].some(value => String(value || '').toLowerCase().includes(keyword))
    return matchesQuery && (role === 'all' || member.role === role || member.activeAssignments?.some(item => item.role === role))
  })

  if (selectedMemberId) return <MemberDetail memberId={selectedMemberId} governance={governance} onBack={() => { setSelectedMemberId(null); void loadDirectory(directoryStatus) }} reload={async () => { await reload(); await loadDirectory(directoryStatus) }} />

  return <div className="page-stack">
    <div className="page-title"><div><p className="eyebrow">部门成员</p><h1>成员</h1><p>查看成员公开档案；管理者可进入详情维护账号、任命和离部信息。</p></div><span className="count-pill">{filtered.length} 人</span></div>
    {message && <div className="alert">{message}</div>}

    <div className="member-toolbar"><div className="member-status-tabs"><button className={directoryStatus === 'active' ? 'active' : ''} onClick={() => setDirectoryStatus('active')}>在职成员</button>{canManage && <><button className={directoryStatus === 'departed' ? 'active' : ''} onClick={() => setDirectoryStatus('departed')}>历史成员</button><button className={directoryStatus === 'disabled' ? 'active' : ''} onClick={() => setDirectoryStatus('disabled')}>停用账号</button></>}</div><div className="member-filters"><input aria-label="搜索成员" placeholder="搜索姓名、学号、学院或班级" value={query} onChange={event => setQuery(event.target.value)} /><select aria-label="按职位筛选" value={role} onChange={event => setRole(event.target.value)}><option value="all">全部职位</option>{Object.entries(roleLabel).map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select></div></div>

    <Section title={directoryStatus === 'active' ? '在职成员名单' : directoryStatus === 'departed' ? '历史成员' : '停用账号'}>
      <div className="member-table-scroll"><div className="member-directory"><div className="member-directory-head"><span>任职</span><span>成员</span><span>学号</span><span>学院</span><span>班级</span><span>联系方式</span><span>状态</span><span>操作</span></div>{filtered.map(member => <button className="member-directory-row" key={member.id} onClick={() => setSelectedMemberId(member.id)}><span className="role-tags member-role-tags">{member.activeAssignments?.length ? member.activeAssignments.map(item => <span className={`role-tag role-${item.role}`} key={item.id}>{item.role_name}</span>) : <span className="role-tag">普通成员</span>}</span><span className="member-main"><strong>{member.fullName}</strong></span><span>{member.studentId || member.username || '-'}</span><span>{member.faculty || '-'}</span><span>{member.className || '-'}</span><span className="member-contact">{member.phone || member.email || '未公开'}</span><span><Badge value={member.status || 'active'} /></span><span className="row-arrow">查看</span></button>)}</div></div>{!filtered.length && <Empty text="没有符合条件的成员" />}
    </Section>

    {canManage && <div className="dashboard-grid">
      <Section title="批量导入成员">
        <form className="stack-form" onSubmit={importMembers}>
          <p className="helper">支持 CSV/XLSX。账号统一使用学号，系统会生成随机初始密码，成员首次登录后必须修改。</p>
          <div className="form-actions left"><button type="button" className="secondary" onClick={() => void downloadTemplate()}>下载导入模板</button></div>
          <label>成员名单文件<input type="file" accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={event => setImportFile(event.target.files?.[0] || null)} /></label>
          <button className="primary" disabled={!importFile}>开始导入</button>
        </form>
      </Section>
      <Section title="申请入口设置">
        <form className="stack-form" onSubmit={saveRegistrationSettings}>
          <label className="toggle-row"><input type="checkbox" checked={settings.allowRegistration} onChange={event => setSettings({ ...settings, allowRegistration: event.target.checked })} />开放成员自主申请</label>
          <label>入部邀请码<input value={settings.inviteCode} onChange={event => setSettings({ ...settings, inviteCode: event.target.value })} placeholder="例如 join2026" /></label>
          <button className="primary">保存设置</button>
        </form>
      </Section>
    </div>}

    {importResult && <Section title="账号分发清单">
      <div className="result-head"><span>已创建 {importResult.createdCount} 人，跳过 {importResult.skippedCount} 行</span><button className="secondary" onClick={downloadDistribution} disabled={!importResult.created.length}>导出分发清单</button></div>
      <div className="table-wrap"><table><thead><tr><th>姓名</th><th>学号/账号</th><th>初始密码</th><th>学院</th><th>专业/班级</th></tr></thead><tbody>{importResult.created.map(user => <tr key={user.id}><td>{user.fullName}</td><td>{user.studentId || user.username}</td><td>{user.initialPassword}</td><td>{user.faculty || '-'}</td><td>{user.className || '-'}</td></tr>)}</tbody></table>{!importResult.created.length && <Empty text="没有创建新账号" />}</div>
      {importResult.skipped.length > 0 && <div className="skip-list">{importResult.skipped.map(item => <div key={`${item.row}-${item.reason}`}><strong>第 {item.row} 行</strong><span>{item.fullName || item.studentId || '未命名'}：{item.reason}</span></div>)}</div>}
    </Section>}

    {canManage && <Section title="入部申请审批">
      <div className="approval-list">{registrationRequests.map(item => <article key={item.id}><div><strong>{item.fullName} · {item.studentId}</strong><p>{item.faculty || '未填学院'} / {item.className || '未填班级'} · {item.phone || item.email || '未填联系方式'}</p><small>{statusLabel[item.status] || item.status} · {dateText(item.createdAt)} · {item.reason || '无申请说明'}</small></div>{item.status === 'pending' && <div><button className="secondary" onClick={() => void reviewRegistration(item.id, 'reject')}>驳回</button><button className="primary" onClick={() => void reviewRegistration(item.id, 'approve')}>通过</button></div>}</article>)}{!registrationRequests.length && <Empty text="暂无入部申请" />}</div>
    </Section>}

  </div>
}

function Approvals({ dashboard, reload }: { dashboard: Dashboard; reload: () => Promise<void> }) {
  const [error, setError] = useState('')
  async function appointment(id: string, action: 'approve' | 'reject') {
    try {
      await api.post('/governance/appointment-requests/' + id + '/' + action, action === 'reject' ? { reason: '未通过当前审核' } : {})
      await reload()
    } catch (err) { setError(errorText(err)) }
  }
  async function taskRequest(id: string, decision: 'approved' | 'rejected') {
    try {
      await api.post('/tasks/requests/' + id + '/resolve', { decision })
      await reload()
    } catch (err) { setError(errorText(err)) }
  }
  async function financeReview(id: string, decision: 'approved' | 'rejected') {
    try {
      await api.post('/finance/' + id + '/review', { decision })
      await reload()
    } catch (err) { setError(errorText(err)) }
  }

  const transactions = dashboard.approvals.transactions || []
  const count = dashboard.approvals.appointments.length + dashboard.approvals.taskRequests.length + transactions.length
  const proxyScope = dashboard.user.activeProxy?.scope
  const canApproveAppointments = dashboard.user.role === 'admin' || dashboard.user.permissions.includes('admin') || ['sensitive', 'full'].includes(proxyScope)
  const canHandleOwnAppointments = dashboard.user.role === 'admin' || dashboard.user.permissions.includes('admin')
  const canApproveAppointment = (item: Appointment) => canApproveAppointments && (item.requested_by !== dashboard.user.id || canHandleOwnAppointments)
  const canApproveFinance = dashboard.user.role === 'admin' || dashboard.user.permissions.includes('admin') || dashboard.user.permissions.includes('finance:approve')
  const financeReasons = (item: any) => Array.isArray(item.approval_reasons)
    ? item.approval_reasons.join('；')
    : (item.approvalReasonText || item.approval_reasons || '需要处理的异常记录')

  return <div className="page-stack">
    <div className="page-title"><div><p className="eyebrow">责任确认</p><h1>审批中心</h1><p>只处理职位变动、任务异常和财务例外；预算内日常收支无需逐笔审批。</p></div><span className="count-pill">{count} 项待处理</span></div>
    {error && <div className="alert error">{error}</div>}
    <Section title="职位任命"><div className="approval-list">{dashboard.approvals.appointments.map(item => <article key={item.id}><div><strong>{item.target_user_name} · {item.requested_role_name}</strong><p>{item.reason || '未填写任命说明'}</p><small>{item.requested_by_name} 发起 · {dateText(item.created_at)}</small></div>{canApproveAppointment(item) ? <div><button className="secondary" onClick={() => void appointment(item.id, 'reject')}>驳回</button><button className="primary" onClick={() => void appointment(item.id, 'approve')}>通过</button></div> : <span className="status status-pending">等待部长或其他有效代理处理</span>}</article>)}{!dashboard.approvals.appointments.length && <Empty text="没有待审批的职位任命" />}</div></Section>
    <Section title="财务例外"><div className="approval-list">{transactions.map((item: any) => <article key={item.id}><div><strong>{item.title} · {money(Number(item.amount || 0))}</strong><p>{financeReasons(item)}</p><small>{item.category || '未分类'} · {dateText(item.date, { dateOnly: true, defaultToday: true })}</small></div>{canApproveFinance ? <div><button className="secondary" onClick={() => void financeReview(item.id, 'rejected')}>驳回</button><button className="primary" onClick={() => void financeReview(item.id, 'approved')}>通过</button></div> : <span className="status status-pending">仅部长或敏感代理可审批</span>}</article>)}{!transactions.length && <Empty text="没有待处理的财务例外" />}</div></Section>
    <Section title="任务异常申请"><div className="approval-list">{dashboard.approvals.taskRequests.map(item => <article key={item.id}><div><strong>{item.task_title} · {statusLabel[item.type] || item.type}</strong><p>{item.reason}</p><small>{item.requester_name} 发起 · {dateText(item.created_at)}</small></div><div><button className="secondary" onClick={() => void taskRequest(item.id, 'rejected')}>驳回</button><button className="primary" onClick={() => void taskRequest(item.id, 'approved')}>通过</button></div></article>)}{!dashboard.approvals.taskRequests.length && <Empty text="没有待处理的任务申请" />}</div></Section>
  </div>
}
async function fileBase64(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192))
  }
  return window.btoa(binary)
}

function RemoteAccessSettings() {
  const [status, setStatus] = useState<RemoteAccessStatus | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [inspection, setInspection] = useState<any>(null)
  const [qrCode, setQrCode] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const stateLabel: Record<string, string> = { not_configured: '未开通', stopped: '已停止', connecting: '连接中', online: '已在线', error: '连接异常' }

  async function load() {
    try { setStatus(await getData<RemoteAccessStatus>('/admin/remote-access/status')) } catch (err) { setError(errorText(err)) }
  }
  useEffect(() => {
    void load()
    const timer = window.setInterval(() => void load(), 10000)
    return () => window.clearInterval(timer)
  }, [])
  useEffect(() => {
    if (!status?.publicUrl) return setQrCode('')
    void QRCode.toDataURL(status.publicUrl, { width: 176, margin: 1, color: { dark: '#173f2d', light: '#ffffff' } }).then(setQrCode)
  }, [status?.publicUrl])

  async function packageBody() {
    if (!file || !password) throw new Error('请选择远程开通包并输入导入密码。')
    return { packageBase64: await fileBase64(file), password }
  }
  async function inspect() {
    setBusy(true); setError(''); setMessage('')
    try {
      const result = await api.post('/admin/remote-access/package/inspect', await packageBody()) as unknown as ApiResult<any>
      setInspection(result.data); setMessage('开通包预检通过，请核对部门和固定网址。')
    } catch (err) { setInspection(null); setError(errorText(err)) } finally { setBusy(false) }
  }
  async function importPackage() {
    if (!inspection || !window.confirm('确认将这个固定网址绑定到当前部门主机？')) return
    setBusy(true); setError('')
    try {
      await api.post('/admin/remote-access/package/import', await packageBody())
      setMessage('远程开通包已导入，系统正在连接固定网址。')
      setFile(null); setPassword(''); setInspection(null); await load()
    } catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }
  async function command(action: 'start' | 'stop' | 'test') {
    setBusy(true); setError(''); setMessage('')
    try {
      await api.post(`/admin/remote-access/${action}`)
      setMessage(action === 'start' ? '正在建立远程连接。' : action === 'stop' ? '远程访问已停止，局域网仍可使用。' : '固定网址连通性检查通过。')
      await load()
    } catch (err) { setError(errorText(err)); await load() } finally { setBusy(false) }
  }

  return <div className="page-stack">
    <div className="page-title"><div><p className="eyebrow">统一远程入口</p><h1>系统设置</h1><p>局域网默认可用，导入发行方提供的开通包后才启用固定网址。</p></div>{status && <span className={`status remote-${status.state}`}>{stateLabel[status.state]}</span>}</div>
    {message && <div className="alert">{message}</div>}{error && <div className="alert error">{error}</div>}
    <div className="dashboard-grid remote-access-grid">
      <Section title="远程访问状态">
        <div className="system-facts"><div><span>固定网址</span><strong>{status?.publicUrl || '尚未开通'}</strong></div><div><span>部门标识</span><strong>{status?.departmentSlug || '-'}</strong></div><div><span>最近在线</span><strong>{dateText(status?.lastOnlineAt)}</strong></div></div>
        {status?.lastError && <div className="alert error remote-error">{status.lastError}</div>}
        <div className="remote-actions"><button className="primary" disabled={busy || !status?.configured || status.state === 'online'} onClick={() => void command('start')}>启动</button><button className="secondary" disabled={busy || !status?.configured || status.state === 'stopped'} onClick={() => void command('stop')}>停止</button><button className="secondary" disabled={busy || !status?.configured} onClick={() => void command('test')}>测试连接</button></div>
        {status && !status.binaryAvailable && <p className="helper">当前发行包缺少 cloudflared 组件，请重新生成便携包。</p>}
        {status && !status.bridgeAvailable && <p className="helper">本机远程桥接端口 {status.bridgePort} 被占用，请关闭其他系统实例后重试。局域网使用不受影响。</p>}
      </Section>
      <Section title="成员访问入口">
        {status?.publicUrl ? <div className="remote-entry">{qrCode && <img src={qrCode} alt="固定网址二维码" />}<div><strong>{status.publicUrl}</strong><p>电脑和手机均使用该网址，更换主机后也不需要更换。</p><button className="secondary" onClick={() => void navigator.clipboard.writeText(status.publicUrl!)}>复制网址</button></div></div> : <Empty text="导入远程开通包后显示固定网址" />}
      </Section>
    </div>
    <Section title="导入远程开通包">
      <div className="form-grid remote-package-form"><label>开通包<input type="file" accept=".remote-access,application/json" onChange={event => { setFile(event.target.files?.[0] || null); setInspection(null) }} /></label><label>一次性导入密码<input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="off" /></label><div className="form-actions span-2"><button className="secondary" disabled={busy || !file || !password} onClick={() => void inspect()}>先预检</button></div></div>
      {inspection && <div className="inspect remote-inspect"><strong>{inspection.departmentName}</strong><span>固定网址：{inspection.publicUrl}</span><span>部署编号：{inspection.deploymentId}</span><span>开通包有效期：{dateText(inspection.expiresAt)}</span><button className="primary" disabled={busy} onClick={() => void importPackage()}>确认导入并连接</button></div>}
    </Section>
  </div>
}

function Handover({ dashboard, governance, reload, cloudMode }: { dashboard: Dashboard; governance: Governance | null; reload: () => Promise<void>; cloudMode: boolean }) {
  const [backups, setBackups] = useState<Backup[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [inspection, setInspection] = useState<any>(null)
  const [message, setMessage] = useState('')
  const [view, setView] = useState<'documents' | 'proxy' | 'data'>('documents')
  const [proxy, setProxy] = useState({ proxyUserId: '', scope: 'sensitive', startsAt: '', endsAt: '', reason: '' })
  async function load() { try { setBackups(await getData<Backup[]>('/admin/backup/list')) } catch (err) { setMessage(errorText(err)) } }
  useEffect(() => { void load() }, [])
  async function backup() { try { await api.post('/admin/backup/create'); setMessage('手动备份已创建。'); await load() } catch (err) { setMessage(errorText(err)) } }
  async function download() { try { const blob = await api.get('/admin/handover/export', { responseType: 'blob' }) as unknown as Blob; downloadBlob(blob, `部门交接包_${new Date().toISOString().slice(0, 10)}.zip`) } catch (err) { setMessage(errorText(err)) } }
  async function inspect() { if (!file) return; try { const result = await api.post('/admin/handover/inspect', await file.arrayBuffer(), { headers: { 'Content-Type': 'application/zip' } }) as unknown as ApiResult<any>; setInspection(result.data); setMessage('交接包预检通过，请核对后再导入。') } catch (err) { setInspection(null); setMessage(errorText(err)) } }
  async function importPackage() { if (!file || !inspection || !window.confirm('导入会覆盖当前数据和附件，系统会先自动备份。确认继续？')) return; try { await api.post('/admin/handover/import', await file.arrayBuffer(), { headers: { 'Content-Type': 'application/zip' } }); setMessage('交接包导入成功，请关闭启动窗口并重新打开系统。') } catch (err) { setMessage(errorText(err)) } }
  async function createProxy(event: FormEvent) { event.preventDefault(); try { await api.post('/governance/proxies', proxy); setMessage('临时代理已生效。'); setProxy({ proxyUserId: '', scope: 'sensitive', startsAt: '', endsAt: '', reason: '' }); await reload() } catch (err) { setMessage(errorText(err)) } }
  async function revokeProxy(id: string) { if (!window.confirm('确认提前终止这项临时代理？')) return; try { await api.post(`/governance/proxies/${id}/revoke`); setMessage('临时代理已终止。'); await reload() } catch (err) { setMessage(errorText(err)) } }
  if (cloudMode && view === 'data') return <div className="page-stack"><div className="page-title"><div><p className="eyebrow">部门传承</p><h1>资料与交接</h1><p>云端数据按部门隔离，恢复与覆盖导入由平台管理员统一执行。</p></div></div>{message && <div className="alert">{message}</div>}<div className="schedule-tabs handover-tabs"><button onClick={() => setView('documents')}>部门资料</button><button onClick={() => setView('proxy')}>临时代理</button><button className="active">数据交接</button></div><div className="dashboard-grid"><Section title="部门归档导出"><div className="stack-form"><p>导出当前部门的成员、任职、任务、排班、活动、财务、审计与附件，供换届交接和留档。</p><button className="primary" onClick={() => void download()}>下载部门归档包</button></div></Section><Section title="云端备份策略"><div className="system-facts"><div><span>存储引擎</span><strong>MariaDB</strong></div><div><span>部门数据量</span><strong>{(dashboard.system.databaseSize / 1024).toFixed(1)} KB</strong></div><div><span>恢复权限</span><strong>仅平台管理员</strong></div></div><p className="muted">服务器执行每日、每周和每月加密备份；部门端不能覆盖导入，避免误操作影响线上数据。</p></Section></div></div>
  return <div className="page-stack"><div className="page-title"><div><p className="eyebrow">部门传承</p><h1>资料与交接</h1><p>统一管理部门资料、临时代理和完整数据交接。</p></div></div>{message && <div className="alert">{message}</div>}<div className="schedule-tabs handover-tabs"><button className={view === 'documents' ? 'active' : ''} onClick={() => setView('documents')}>部门资料</button><button className={view === 'proxy' ? 'active' : ''} onClick={() => setView('proxy')}>临时代理</button><button className={view === 'data' ? 'active' : ''} onClick={() => setView('data')}>数据交接</button></div>{view === 'documents' && <div className="dashboard-grid"><Section title="部门基本信息"><div className="system-facts"><div><span>部门名称</span><strong>{dashboard.department.name}</strong></div><div><span>所属单位</span><strong>{dashboard.department.faculty || '未设置'}</strong></div><div><span>当前部长</span><strong>{dashboard.department.headName || '未设置'}</strong></div></div></Section><Section title="任职概况"><div className="system-facts"><div><span>在职成员</span><strong>{dashboard.metrics.members}</strong></div><div><span>有效任职</span><strong>{governance?.assignments.length || 0}</strong></div><div><span>有效代理</span><strong>{governance?.proxies.length || 0}</strong></div></div></Section></div>}{view === 'proxy' && <div className="dashboard-grid"><Section title="有效临时代理"><div className="compact-list">{governance?.proxies.map(item => <div className="compact-row" key={item.id}><div><strong>{item.proxy_user_name}</strong><small>{item.scope === 'light' ? '日常调度' : item.scope === 'full' ? '完整代理' : '敏感审批'} · {dateText(item.starts_at)} 至 {dateText(item.ends_at)}</small><small>{item.granted_by_name} 授权 · {item.reason || '未填写原因'}</small></div><div className="row-actions"><Badge value={item.status} /><button className="text-btn danger-text" onClick={() => void revokeProxy(item.id)}>终止</button></div></div>)}{!governance?.proxies.length && <Empty text="当前没有有效临时代理" />}</div></Section><Section title="设置临时代理"><form className="stack-form" onSubmit={createProxy}><label>代理成员<select value={proxy.proxyUserId} onChange={event => setProxy({ ...proxy, proxyUserId: event.target.value })} required><option value="">请选择</option>{governance?.users.filter(item => item.role !== 'admin').map(item => <option value={item.id} key={item.id}>{item.fullName}</option>)}</select></label><label>代理范围<select value={proxy.scope} onChange={event => setProxy({ ...proxy, scope: event.target.value })}><option value="light">日常调度</option><option value="sensitive">敏感审批</option><option value="full">完整代理</option></select></label><label>开始时间<input type="datetime-local" value={proxy.startsAt} onChange={event => setProxy({ ...proxy, startsAt: event.target.value })} /></label><label>结束时间<input type="datetime-local" value={proxy.endsAt} onChange={event => setProxy({ ...proxy, endsAt: event.target.value })} required /></label><label>代理原因<input value={proxy.reason} onChange={event => setProxy({ ...proxy, reason: event.target.value })} required /></label><button className="primary">启用代理</button></form></Section></div>}{view === 'data' && <><div className="handover-actions"><button className="action-block" onClick={() => void backup()}><strong>创建即时备份</strong><span>保存当前数据库快照</span></button><button className="action-block" onClick={() => void download()}><strong>导出部门交接包</strong><span>包含成员、任务、排班、财务和附件</span></button></div><div className="dashboard-grid"><Section title="导入交接包"><div className="stack-form"><label>选择 ZIP 文件<input type="file" accept=".zip,application/zip" onChange={event => { setFile(event.target.files?.[0] || null); setInspection(null) }} /></label><button className="secondary" disabled={!file} onClick={() => void inspect()}>先预检</button>{inspection && <div className="inspect"><strong>{inspection.departmentName}</strong><span>导出时间：{dateText(inspection.exportedAt)}</span><span>附件：{inspection.uploadCount} 个</span><span>校验：{inspection.checksumMatched ? '通过' : '失败'}</span><button className="danger" onClick={() => void importPackage()}>确认覆盖并导入</button></div>}</div></Section><Section title="备份状态"><div className="system-facts"><div><span>数据库大小</span><strong>{(dashboard.system.databaseSize / 1024).toFixed(1)} KB</strong></div><div><span>备份数量</span><strong>{backups.length}</strong></div><div><span>最近更新</span><strong>{dateText(dashboard.system.databaseUpdatedAt)}</strong></div></div><div className="backup-list">{backups.slice(0, 5).map(item => <div key={item.filename}><span>{item.filename}</span><small>{(item.size / 1024).toFixed(1)} KB</small></div>)}</div></Section></div></>}</div>
}

function DepartmentProfileDialog({ department, onClose, onSaved }: { department: DepartmentProfile; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: department.name || '', faculty: department.faculty || '', description: department.description || '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true); setError('')
    try {
      await api.put('/departments', form)
      onSaved()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return <div className="modal"><form className="dialog stack-form" onSubmit={submit}>
    <div className="dialog-head"><div><p className="eyebrow">部门资料</p><h2>设置部门名称</h2></div><button type="button" className="text-btn" onClick={onClose}>关闭</button></div>
    <p>“部务云”是产品名称。这里填写你们部门在系统中显示的名称，成员看到的资料会同步更新。</p>
    {error && <div className="alert error">{error}</div>}
    <label>部门名称<input value={form.name} maxLength={80} onChange={event => setForm({ ...form, name: event.target.value })} required autoFocus /></label>
    <label>所属单位（可选）<input value={form.faculty} maxLength={80} onChange={event => setForm({ ...form, faculty: event.target.value })} /></label>
    <label>部门简介（可选）<textarea value={form.description} maxLength={500} onChange={event => setForm({ ...form, description: event.target.value })} /></label>
    <div className="dialog-actions"><button type="button" className="secondary" onClick={onClose}>取消</button><button className="primary" disabled={busy}>{busy ? '保存中…' : '保存部门资料'}</button></div>
  </form></div>
}

function DepartmentApp() {
  const { user, token, cloudMode, contexts, setUser, setCsrfToken, logout } = useAuthStore()
  const [tab, setTab] = useState<Tab>('overview')
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [governance, setGovernance] = useState<Governance | null>(null)
  const [activities, setActivities] = useState<Activity[]>([])
  const [finance, setFinance] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [departmentProfileOpen, setDepartmentProfileOpen] = useState(false)
  const [remoteAccessAvailable, setRemoteAccessAvailable] = useState(!cloudMode)

  const permissions = dashboard?.user?.permissions || []
  const has = (permission: string) => permissions.includes('admin') || permissions.includes(permission)
  const isAdmin = dashboard?.user?.role === 'admin' || permissions.includes('admin')
  const visibleTabs = useMemo(() => tabInfo.filter(item => !((cloudMode || !remoteAccessAvailable) && item.key === 'settings')).filter(item => cloudMode || item.key !== 'plan').filter(item => !item.permission || has(item.permission)).filter(item => !item.manager || dashboard?.user?.isManager).filter(item => !item.admin || isAdmin), [permissions.join(','), dashboard?.user?.isManager, isAdmin, cloudMode, remoteAccessAvailable])

  useEffect(() => {
    let disposed = false
    void api.get('/health').then(response => {
      const result = response as unknown as { features?: { remoteAccess?: boolean } }
      if (!disposed) setRemoteAccessAvailable(result.features?.remoteAccess !== false)
    }).catch(() => undefined)
    return () => { disposed = true }
  }, [cloudMode])

  useEffect(() => {
    if (!cloudMode || !token || !user) return
    void api.get('/auth/me')
      .then(result => setUser((result as unknown as ApiResult<User>).data))
      .catch(() => undefined)
  }, [cloudMode, token, user?.departmentId])

  useEffect(() => {
    if (tab === 'settings' && (cloudMode || !remoteAccessAvailable)) setTab('overview')
  }, [tab, cloudMode, remoteAccessAvailable])

  async function refresh() {
    if (!token || !user) return
    setLoading(true)
    setError('')
    try {
      const overview = await getData<Dashboard>('/dashboard/overview')
      setDashboard(overview)
      const requests: Promise<void>[] = [getData<User[]>('/users/directory').then(setUsers)]
      if (overview.user.permissions.includes('tasks:read') || overview.user.permissions.includes('admin')) requests.push(getData<Task[]>('/tasks').then(setTasks))
      if (overview.user.permissions.includes('governance:read') || overview.user.permissions.includes('admin')) requests.push(getData<Governance>('/governance/positions').then(setGovernance))
      if (overview.user.permissions.includes('activities:read') || overview.user.permissions.includes('admin')) requests.push(getData<Activity[]>('/activities').then(setActivities))
      if (overview.user.permissions.includes('finance:read') || overview.user.permissions.includes('admin')) requests.push(getData<Transaction[]>('/finance?limit=100').then(setFinance))
      await Promise.all(requests)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }

  async function changeDepartment(departmentId: string) {
    if (!departmentId || departmentId === user?.departmentId) return
    setLoading(true); setError('')
    try {
      const result = await api.post('/auth/context', { departmentId }) as unknown as ApiResult<{ user: User; csrfToken: string }>
      setCsrfToken(result.data.csrfToken)
      setUser(result.data.user)
      setDashboard(null); setTasks([]); setUsers([]); setGovernance(null); setActivities([]); setFinance([]); setTab('overview')
      await refresh()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }

  async function exitDepartment() {
    try {
      if (cloudMode) await api.post('/auth/logout')
    } catch {
      // Local state must still be cleared when the server is unavailable.
    } finally {
      logout()
    }
  }

  useEffect(() => { void refresh() }, [token, user?.departmentId])
  useEffect(() => {
    if (!token) return
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    window.addEventListener('focus', refreshIfVisible)
    document.addEventListener('visibilitychange', refreshIfVisible)
    const timer = window.setInterval(refreshIfVisible, 60000)
    return () => {
      window.removeEventListener('focus', refreshIfVisible)
      document.removeEventListener('visibilitychange', refreshIfVisible)
      window.clearInterval(timer)
    }
  }, [token])
  useEffect(() => {
    if (dashboard && !visibleTabs.some(item => item.key === tab)) setTab('overview')
  }, [dashboard, visibleTabs, tab])
  if (!token) return <Login />
  if (!user && cloudMode && contexts.length) return <DepartmentChooser />
  if (!user) return <Login />
  if (cloudMode && user.accountDeletionPending) return <AccountDeletionGate onCancelled={() => {
    void api.get('/auth/me').then(result => setUser((result as unknown as ApiResult<User>).data))
  }} onExit={() => void exitDepartment()} />
  if (cloudMode && user.legalAcceptanceRequired) return <LegalGate onAccepted={() => {
    setUser({ ...user, legalAcceptanceRequired: false })
  }} onExit={() => void exitDepartment()} />
  if (user.mustChangePassword) return <PasswordGate user={user} onDone={setUser} onExit={() => void exitDepartment()} />
  if (cloudMode && user.departmentStatus === 'pending_verification') {
    return <DepartmentVerificationGate userName={user.fullName} onExit={() => void exitDepartment()} />
  }

  return <div className="app-shell">
    <aside className={menuOpen ? 'sidebar open' : 'sidebar'}>
      {cloudMode && contexts.length > 1 && <label className="department-switcher"><span>当前部门</span><select value={user.departmentId || ''} onChange={event => void changeDepartment(event.target.value)}>{contexts.map(context => <option value={context.departmentId} key={context.departmentId}>{context.departmentName}</option>)}</select></label>}
      <div className="brand"><span className="brand-seal">部</span><div><strong>部务云</strong><small>{dashboard?.department.name || '校园部门'}</small></div></div>
      <nav>{visibleTabs.map(item => <button key={item.key} className={tab === item.key ? 'active' : ''} onClick={() => { setTab(item.key); setMenuOpen(false) }}><span>{item.mark}</span>{item.label}{item.key === 'approvals' && Number(dashboard?.metrics.pendingApprovals) > 0 && <i>{dashboard?.metrics.pendingApprovals}</i>}</button>)}</nav>
      <div className="sidebar-user"><div className="avatar">{user.fullName.slice(0, 1)}</div><div><strong>{user.fullName}</strong><small>{dashboard?.user?.roleName || roleLabel[user.role]}</small></div><div className="sidebar-actions">{isAdmin && <button title="编辑部门资料" onClick={() => setDepartmentProfileOpen(true)}>部门</button>}<button title="编辑个人资料" onClick={() => setProfileOpen(true)}>资料</button><button title="退出登录" onClick={() => void exitDepartment()}>退出</button></div></div>
    </aside>
      <main className="workspace"><header className="mobile-head"><button title="打开导航" onClick={() => setMenuOpen(value => !value)}>菜单</button><strong>{tabInfo.find(item => item.key === tab)?.label}</strong><button title="刷新" onClick={() => void refresh()}>刷新</button></header><div className="content">{cloudMode && user.verificationStatus && user.verificationStatus !== 'verified' && <div className="trial-banner"><strong>未认证部门</strong><span>当前可在内部试用，但不会出现在公开部门搜索中。部长可在“资料与交接”提交核验资料。</span></div>}{loading && !dashboard && <div className="loading">正在加载部门数据...</div>}{error && <div className="alert error">{error}<button onClick={() => void refresh()}>重试</button></div>}{dashboard && tab === 'overview' && <Overview data={dashboard} go={setTab} />}{dashboard && tab === 'tasks' && <Tasks data={tasks} users={users} canManage={dashboard.user.isManager} reload={refresh} />}{dashboard && tab === 'schedule' && <Schedule dashboard={dashboard} users={users} activities={activities} reload={refresh} />}{tab === 'activities' && <Activities data={activities} users={users} canManage={has('activities:write')} reload={refresh} />}{dashboard && tab === 'finance' && <Finance dashboard={dashboard} data={finance} canWrite={has('finance:write')} reload={refresh} />}{tab === 'members' && <Members members={users} governance={governance} canManage={has('users:write')} reload={refresh} />}{dashboard && tab === 'approvals' && <Approvals dashboard={dashboard} reload={refresh} />}{dashboard && tab === 'handover' && <Handover dashboard={dashboard} governance={governance} reload={refresh} cloudMode={cloudMode} />}{cloudMode && dashboard && tab === 'plan' && <DepartmentBilling />}{dashboard && tab === 'settings' && <RemoteAccessSettings />}</div></main>
    {profileOpen && <ProfileDialog user={user} onClose={() => setProfileOpen(false)} onSaved={(next) => { setUser(next); setProfileOpen(false); void refresh() }} />}
    {departmentProfileOpen && dashboard && <DepartmentProfileDialog department={dashboard.department} onClose={() => setDepartmentProfileOpen(false)} onSaved={() => { setDepartmentProfileOpen(false); void refresh() }} />}
  </div>
}

function IcpFooter() {
  return <footer className="site-footer">
    <span>部务云 · 校园部门协作平台</span>
    <a href="/legal/terms">用户服务协议</a>
    <a href="/legal/privacy">隐私政策</a>
    <a href="mailto:1261086613@qq.com">1261086613@qq.com</a>
    <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">闽ICP备2026015907号-2</a>
  </footer>
}

function App() {
  const path = window.location.pathname
  const page = path === '/legal/terms'
    ? <LegalDocumentPage type="terms" />
    : path === '/legal/privacy'
      ? <LegalDocumentPage type="privacy" />
      : path.startsWith('/platform') ? <PlatformApp /> : <DepartmentApp />
  return <div className="site-root">{page}<IcpFooter /></div>
}

export default App
